import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import CenteredMessage from '../components/CenteredMessage';
import PaginatedTable from '../components/PaginatedTable';
import { useApi, prefetchApi } from '../hooks/useApi';
import { API_BASE_URL } from '../config/env';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/trazabilidad-recetas-premium.css';
import '../styles/recetas-cruzadas-premium.css';
import '../styles/asociacion-clientes-premium.css';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import { CrmStatusBadgeFrom } from '../components/CrmStatusBadge';
import { resolveClienteStatusBadge } from '../utils/crmStatusBadge';
import {
  PULSE_CMD_OPEN_WA_KEY,
  PULSE_CMD_WA_SEARCH_KEY
} from '../config/pulseCommand';

const PAGE_SIZE = 10;
const TIMELINE_DEFAULT_PAGE_SIZE = 5;
const REC_DEFAULT_PAGE_SIZE = 10;
const VINCULO_TIPOS_DEFAULT = ['Padre'];

function getDniMeta(dni) {
  if (!dni) return { formatted: null, digits: null };
  const digits = String(dni)
    .trim()
    .replace(/^0+/, '')
    .replace(/\D/g, '');
  if (!digits) return { formatted: null, digits: null };
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return { formatted, digits };
}

function formatDni(dni) {
  return getDniMeta(dni).formatted;
}

function formatDate(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-AR');
}

function formatDateTime(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Fecha/hora en 24 h sin "a. m." / "p. m." (vínculos, etc.) */
function formatDateTime24(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
    .replace(/\s*(?:a\.?\s*m\.?|p\.?\s*m\.?)\s*\.?\s*$/i, '')
    .trim();
}

function formatPrestadorLabel(prestador) {
  if (!prestador) return null;
  return prestador
    .toLowerCase()
    .split(' ')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
    .replace(/^(\w+)\s(.+)$/, '$1, $2');
}

function isPlexItemAnulada(it) {
  return it?.id_global_nc != null || it?.ref_id_nc != null || it?.ref_orden_nc != null;
}

function RecetasCountBadge({ filteredCount, totalCount, itemCount, searchActive }) {
  const countFiltered = searchActive && filteredCount !== totalCount;
  return (
    <span
      className={`crm-ac-rec-section__count${countFiltered ? ' crm-ac-rec-section__count--filtered' : ''}`}
    >
      {countFiltered ? (
        <>
          {filteredCount} de {totalCount} receta{totalCount === 1 ? '' : 's'}
        </>
      ) : (
        <>
          {totalCount} receta{totalCount === 1 ? '' : 's'}
        </>
      )}
      {' · '}
      {itemCount} ítem{itemCount === 1 ? '' : 's'}
    </span>
  );
}

function AcRecetasToolbar({
  variant,
  icon,
  title,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  filteredCount,
  totalCount,
  itemCount,
  searchActive,
  loading,
  toolbarOnly = false
}) {
  return (
    <div
      className={`crm-ac-rec-section__toolbar crm-ac-rec-section__toolbar--${variant}${
        toolbarOnly ? ' crm-ac-rec-section__toolbar--compact' : ''
      }`}
    >
      {!toolbarOnly ? (
      <div className="crm-ac-rec-section__head">
        <span className="crm-ac-rec-section__icon" aria-hidden="true">
          {icon}
        </span>
        <h3 className="crm-ac-rec-section__title">{title}</h3>
        <RecetasCountBadge
          filteredCount={filteredCount}
          totalCount={totalCount}
          itemCount={itemCount}
          searchActive={searchActive}
        />
        {loading ? <span className="crm-ac-rec-section__spinner" aria-hidden="true" /> : null}
      </div>
      ) : null}
      <div className="crm-ac-rec-section__search">
        <span className="crm-ac-rec-section__search-icon" aria-hidden="true">
          🔎
        </span>
        <input
          type="search"
          className="crm-ac-rec-section__search-input"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={searchPlaceholder}
        />
        {searchValue.trim() ? (
          <button
            type="button"
            className="crm-ac-rec-section__search-clear"
            onClick={() => onSearchChange('')}
            aria-label="Limpiar búsqueda"
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="crm-ac-rec-section__meta">
        <span className="crm-ac-rec-section__meta-label">Mostrar</span>
        <select
          value={pageSize <= 0 ? 'ALL' : String(pageSize)}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'ALL') onPageSizeChange(0);
            else {
              const n = parseInt(v, 10);
              onPageSizeChange(Number.isNaN(n) ? REC_DEFAULT_PAGE_SIZE : n);
            }
          }}
          aria-label="Registros por página"
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="ALL">Todos</option>
        </select>
        <span className="crm-ac-rec-section__meta-label">por página</span>
      </div>
    </div>
  );
}

function AcRecetasEmpty({ variant, mode, searchTerm, onClearSearch }) {
  const isSan = variant === 'san';
  const icon = mode === 'no-match' ? '🔎' : isSan ? '🏥' : '💊';
  const title =
    mode === 'no-match'
      ? 'Sin coincidencias'
      : isSan
        ? 'Sin recetas emitidas'
        : 'Sin recetas dispensadas';
  const hint =
    mode === 'no-match'
      ? 'Probá con otro ID, producto, fecha o diagnóstico.'
      : isSan
        ? 'No hay recetas GECLISA registradas para este paciente.'
        : 'No hay recetas PLEX registradas para este paciente.';

  return (
    <div
      className={`crm-ac-rec-section__state${
        mode === 'no-match' ? ' crm-ac-rec-section__state--no-match' : ''
      }`}
      role="status"
    >
      <span className="crm-ac-rec-section__state-icon" aria-hidden="true">
        {icon}
      </span>
      <p className="crm-ac-rec-section__state-title">{title}</p>
      {mode === 'no-match' && searchTerm?.trim() ? (
        <p className="crm-ac-rec-section__state-term" title={searchTerm.trim()}>
          {searchTerm.trim().length > 40
            ? `${searchTerm.trim().slice(0, 40)}…`
            : searchTerm.trim()}
        </p>
      ) : null}
      <p className="crm-ac-rec-section__state-hint">{hint}</p>
      {mode === 'no-match' && onClearSearch ? (
        <button type="button" className="crm-ac-rec-section__state-btn" onClick={onClearSearch}>
          Limpiar búsqueda
        </button>
      ) : null}
    </div>
  );
}

function formatKpiNum(value) {
  if (value == null || value === '—') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('es-AR');
}

function AcKpiIcon({ tone }) {
  if (tone === 'cruce') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (tone === 'farm') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3v18M8 7h8M7 21h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="5" y="11" width="14" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (tone === 'san') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 7v10M7 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <rect x="4" y="5" width="16" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AcTableLoading({ searchTerm = '', variant = 'default' }) {
  const trimmed = String(searchTerm || '').trim();
  const displayTerm = trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
  const isSearch = Boolean(trimmed);
  const isWa = variant === 'wa';

  return (
    <div className="crm-ac-table-loading" role="status" aria-live="polite" aria-busy="true">
      <div className="crm-ac-table-loading__glow" aria-hidden="true" />
      <div className="crm-ac-table-loading__inner">
        <div className="crm-ac-table-loading__visual" aria-hidden="true">
          <span className="crm-ac-table-loading__orbit crm-ac-table-loading__orbit--outer" />
          <span className="crm-ac-table-loading__orbit crm-ac-table-loading__orbit--inner" />
          <span className="crm-ac-table-loading__glyph">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M15.5 15.5L20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M7 10.5h7M10.5 7v7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.55" />
            </svg>
          </span>
        </div>
        <p className="crm-ac-table-loading__eyebrow">{isWa ? 'WhatsApp · GECLISA' : 'PLEX ↔ GECLISA'}</p>
        <h2 className="crm-ac-table-loading__title">
          {isSearch
            ? 'Buscando en ambas bases'
            : isWa
              ? 'Cargando altas WhatsApp'
              : 'Cargando asociación'}
        </h2>
        {isSearch ? (
          <div className="crm-ac-table-loading__query" title={trimmed}>
            <span className="crm-ac-table-loading__query-ic" aria-hidden="true">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="crm-ac-table-loading__query-text">{displayTerm}</span>
          </div>
        ) : null}
        <p className="crm-ac-table-loading__hint">
          {isSearch
            ? isWa
              ? 'Filtrando altas de WhatsApp pendientes de vínculo…'
              : 'Cruzando pacientes entre farmacia PLEX y sanatorio GECLISA…'
            : isWa
              ? 'Obteniendo registros del canal digital…'
              : 'Preparando el cruce de clientes, coberturas y últimos movimientos…'}
        </p>
        <div className="crm-ac-table-loading__bar" aria-hidden="true">
          <span className="crm-ac-table-loading__bar-fill" />
        </div>
        <div className="crm-ac-table-loading__tags" aria-hidden="true">
          <span className="crm-ac-table-loading__tag">PLEX</span>
          <span className="crm-ac-table-loading__tag">GECLISA</span>
          <span className="crm-ac-table-loading__tag">Coberturas</span>
        </div>
      </div>
    </div>
  );
}

function AcEmptyHero({ eyebrow, title, hint, steps = [], error = false, compact = false }) {
  return (
    <div
      className={`crm-rc-empty-hero${error ? ' crm-rc-empty-hero--error' : ''}${compact ? ' crm-ac-empty-hero--compact' : ''}`}
      role={error ? 'alert' : 'status'}
    >
      <div className="crm-rc-empty-hero__inner">
        <div
          className={`crm-rc-empty-hero__visual${error ? ' crm-rc-empty-hero__visual--error' : ''}`}
          aria-hidden="true"
        >
          <span className="crm-rc-empty-hero__orbit" />
          <span className="crm-rc-empty-hero__glyph">
            {error ? (
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 7v6M12 16.5h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="5" width="16" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M9 5V3.5M15 5V3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </span>
        </div>
        {eyebrow ? <p className="crm-rc-empty-hero__eyebrow">{eyebrow}</p> : null}
        <h2 className="crm-rc-empty-hero__title">{title}</h2>
        {hint ? <p className="crm-rc-empty-hero__hint">{hint}</p> : null}
        {steps.length > 0 ? (
          <div className="crm-rc-empty-hero__steps" aria-hidden="true">
            {steps.map((step) => (
              <span key={step} className="crm-rc-empty-hero__step">
                {step}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AcRecetasPager({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <nav className="crm-ac-rec-section__pager" aria-label="Paginación de recetas">
      <button
        type="button"
        className="crm-ac-rec-section__pager-btn"
        onClick={onPrev}
        disabled={page === 1}
        aria-label="Página anterior"
      >
        ‹
      </button>
      <span className="crm-ac-rec-section__pager-meta">
        Página <strong>{page}</strong> de {totalPages}
      </span>
      <button
        type="button"
        className="crm-ac-rec-section__pager-btn"
        onClick={onNext}
        disabled={page === totalPages}
        aria-label="Página siguiente"
      >
        ›
      </button>
    </nav>
  );
}

function PlexItemStatus({ anulada }) {
  return (
    <span
      className={`crm-ac-rec-status${anulada ? ' crm-ac-rec-status--void' : ' crm-ac-rec-status--ok'}`}
      title={anulada ? 'Nota de crédito (NC) · Anulada' : 'Facturada (vigente)'}
    >
      {anulada ? 'NC · ANULADA' : 'FACTURADA'}
    </span>
  );
}

/** Fecha de registro en BD (UTC) → fecha Argentina (−3 h), sin hora. */
function formatDateAr(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  d.setTime(d.getTime() - 3 * 60 * 60 * 1000);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function canOpenWaFicha(row) {
  const dni = String(row?.dni_normalizado || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');
  return dni.length >= 7 && dni.length <= 10;
}

function defaultWaDateRange() {
  const hasta = new Date();
  const desde = new Date();
  desde.setDate(desde.getDate() - 30);
  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { desde: iso(desde), hasta: iso(hasta) };
}

function mapWaRowToCliente(row) {
  if (!row) return null;
  return {
    ...row,
    id: row.id_unico ?? `dni:${row.dni_normalizado || ''}`,
    __movimientos: [],
    __contactosGeclisa: uniqueNonEmpty([row.telefono_geclisa, row.celular_geclisa]),
    __contactosPlex: uniqueNonEmpty([row.telefono_plex]),
    __emailsGeclisa: uniqueNonEmpty([row.email_geclisa]),
    __emailsPlex: uniqueNonEmpty([row.email_plex]),
    __allRows: [row]
  };
}

function getWaPlexDisplayName(row) {
  return String(row?.nombre_completo || '').trim() || 'Sin nombre';
}

function mapWaPlexRowToCliente(row) {
  if (!row) return null;
  return {
    ...row,
    nombre_plex: row.nombre_completo || null,
    telefono_plex: row.telefono || null,
    email_plex: row.email || null,
    id: `ext-${row.id}`,
    __movimientos: [],
    __contactosGeclisa: [],
    __contactosPlex: uniqueNonEmpty([row.telefono]),
    __emailsGeclisa: [],
    __emailsPlex: uniqueNonEmpty([row.email]),
    __allRows: [row]
  };
}

function uniqueNonEmpty(values) {
  const set = new Set();
  values.forEach(v => {
    const clean = (v || '').toString().trim();
    if (clean) set.add(clean);
  });
  return Array.from(set);
}

function getDisplayName(row) {
  const externo = (row.nombre_completo || '').trim();
  if (externo) return externo;
  const apellido = (row.apellido_geclisa || '').trim();
  const nombre = (row.nombre_geclisa || '').trim();
  const nombreGeclisa = [apellido, nombre].filter(Boolean).join(', ');
  const nombrePlex = (row.nombre_plex || '').trim();
  return nombreGeclisa || nombrePlex || 'Sin nombre';
}

function getInitials(row) {
  const name = getDisplayName(row);
  return name.split(' ').map(x => x[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

function openRecetaPrintWindow({ title, paciente, recetaInfo, items, columns }) {
  if (typeof window === 'undefined') return;

  const today = new Date();
  const formattedToday = today.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const safe = (v) => (v == null || v === '' ? '—' : String(v));

  const headCells = columns.map(c => `<th>${c.header}</th>`).join('');
  const rowsHtml = items.map(it => {
    const tds = columns.map(c => `<td>${safe(typeof c.accessor === 'function' ? c.accessor(it) : it[c.accessor])}</td>`).join('');
    return `<tr>${tds}</tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
  <html lang="es">
  <head>
    <meta charSet="utf-8" />
    <title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      body {
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        margin: 0;
        padding: 24px;
        background: #f3f4f6;
        color: #111827;
      }
      .sheet {
        max-width: 900px;
        margin: 0 auto;
        background: #ffffff;
        border-radius: 12px;
        padding: 24px 28px;
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
      }
      .hdr {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 16px;
        margin-bottom: 16px;
      }
      .hdr-left {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .avatar {
        width: 40px;
        height: 40px;
        border-radius: 999px;
        background: #334155;
        color: #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
      }
      .pt-name {
        font-size: 16px;
        font-weight: 700;
      }
      .pt-meta {
        font-size: 12px;
        color: #6b7280;
        margin-top: 2px;
      }
      .hdr-right {
        text-align: right;
        font-size: 12px;
        color: #6b7280;
      }
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 9px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        margin-top: 4px;
      }
      .badge-plex {
        background: #eff6ff;
        color: #1d4ed8;
        border: 1px solid #bfdbfe;
      }
      .badge-gec {
        background: #ecfdf3;
        color: #15803d;
        border: 1px solid #bbf7d0;
      }
      .sec-title {
        font-size: 13px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: .06em;
        color: #6b7280;
        margin-bottom: 6px;
      }
      .rec-meta {
        font-size: 13px;
        margin-bottom: 16px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
      }
      thead th {
        text-align: left;
        padding: 8px 10px;
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
        color: #4b5563;
      }
      tbody td {
        padding: 7px 10px;
        border-bottom: 1px solid #f3f4f6;
      }
      tbody tr:nth-child(even) td {
        background: #fafafa;
      }
      .ft {
        margin-top: 18px;
        font-size: 11px;
        color: #9ca3af;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .print-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        color: #374151;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
      }
      .print-btn:hover {
        background: #f3f4f6;
      }
      @media print {
        body { background: #ffffff; padding: 0; }
        .sheet { box-shadow: none; border-radius: 0; max-width: none; }
        .print-btn { display: none; }
      }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="hdr">
        <div class="hdr-left">
          <div class="avatar">${paciente.iniciales}</div>
          <div>
            <div class="pt-name">${safe(paciente.nombre)}</div>
            <div class="pt-meta">${safe(paciente.meta)}</div>
          </div>
        </div>
        <div class="hdr-right">
          <div>${title}</div>
          <div>${formattedToday}</div>
          <div class="badge ${recetaInfo.origen === 'PLEX' ? 'badge-plex' : 'badge-gec'}">
            <span>${recetaInfo.origen === 'PLEX' ? '💊 Receta PLEX' : '🏥 Receta GECLISA'}</span>
            ${recetaInfo.idVisible ? `<span>#${recetaInfo.idVisible}</span>` : ''}
          </div>
        </div>
      </div>

      <div class="rec-meta">
        ${recetaInfo.texto ? `<div>${recetaInfo.texto}</div>` : ''}
        ${recetaInfo.obraSocial ? `<div><strong>Obra social:</strong> ${safe(recetaInfo.obraSocial)}</div>` : ''}
        ${recetaInfo.prestador ? `<div><strong>Prestador:</strong> ${safe(recetaInfo.prestador)}</div>` : ''}
      </div>

      <div class="sec-title">Detalle de medicación</div>
      <table>
        <thead><tr>${headCells}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>

      <div class="ft">
        <span>Documento generado desde panel clínico</span>
        <button class="print-btn" onclick="window.print()">
          <span>🖨</span>
          <span>Imprimir / Guardar PDF</span>
        </button>
      </div>
    </div>
  </body>
  </html>`;

  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (e) {
    // Fallback muy simple si algo falla con Blob/URL
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
}

function AsociacionClientes() {
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pendingOpenDni, setPendingOpenDni] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [profileTab, setProfileTab] = useState('resumen');
  const [resumenAccordion, setResumenAccordion] = useState('datos');
  const [recetasAccordion, setRecetasAccordion] = useState('farm');
  const [histFilter, setHistFilter] = useState('all');
  const [movCountFilter, setMovCountFilter] = useState(null);
  const [histSearch, setHistSearch] = useState('');
  const [histPage, setHistPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelinePageSize, setTimelinePageSize] = useState(TIMELINE_DEFAULT_PAGE_SIZE);
  const [timelineSearch, setTimelineSearch] = useState('');
  const [recGePage, setRecGePage] = useState(1);
  const [recPlexPage, setRecPlexPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(10);
  const [recGePageSize, setRecGePageSize] = useState(REC_DEFAULT_PAGE_SIZE);
  const [recPlexPageSize, setRecPlexPageSize] = useState(REC_DEFAULT_PAGE_SIZE);
  const [medSearch, setMedSearch] = useState('');
  const [recGeSearch, setRecGeSearch] = useState('');
  const [recPlexSearch, setRecPlexSearch] = useState('');
  const [tablePageSize, setTablePageSize] = useState(PAGE_SIZE);
  const [kpisPopupOpen, setKpisPopupOpen] = useState(false);
  const [waPopupOpen, setWaPopupOpen] = useState(false);
  const [waPage, setWaPage] = useState(1);
  const [waPageSize, setWaPageSize] = useState(10);
  const [waDesde, setWaDesde] = useState(() => defaultWaDateRange().desde);
  const [waHasta, setWaHasta] = useState(() => defaultWaDateRange().hasta);
  const [waSearch, setWaSearch] = useState('');
  const [waApplied, setWaApplied] = useState({ desde: defaultWaDateRange().desde, hasta: defaultWaDateRange().hasta, search: '' });
  const [waPlexPopupOpen, setWaPlexPopupOpen] = useState(false);
  const [waPlexPage, setWaPlexPage] = useState(1);
  const [waPlexPageSize, setWaPlexPageSize] = useState(10);
  const [waPlexDesde, setWaPlexDesde] = useState(() => defaultWaDateRange().desde);
  const [waPlexHasta, setWaPlexHasta] = useState(() => defaultWaDateRange().hasta);
  const [waPlexSearch, setWaPlexSearch] = useState('');
  const [waPlexApplied, setWaPlexApplied] = useState({
    desde: defaultWaDateRange().desde,
    hasta: defaultWaDateRange().hasta,
    search: ''
  });
  const [vinculoSearch, setVinculoSearch] = useState('');
  const [selectedVinculoClienteId, setSelectedVinculoClienteId] = useState('');
  const [selectedVinculoTipo, setSelectedVinculoTipo] = useState('');
  const [vinculoSaving, setVinculoSaving] = useState(false);
  const [vinculoFeedback, setVinculoFeedback] = useState('');
  const [vinculosReloadKey, setVinculosReloadKey] = useState(0);
  const [editingVinculoId, setEditingVinculoId] = useState(null);
  const [editingVinculoTipo, setEditingVinculoTipo] = useState('');

  const showToast = (message, type = 'info') => {
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      text: message,
      showConfirmButton: false,
      timer: 3200,
      timerProgressBar: true,
      icon: type,
      customClass: { popup: 'swal-toast-crm' },
      background: '#fff'
    });
  };

  const triggerSearchNow = () => {
    const raw = (search || '').trim();
    const isDniLike = /^[\d.]+$/.test(raw);

    let normalized;
    if (isDniLike) {
      normalized = raw.replace(/\D/g, '');
    } else {
      // Si no es DNI y no tiene coma pero sí espacio(s),
      // asumimos formato "APELLIDO NOMBRE" y lo convertimos a "APELLIDO, NOMBRE"
      if (!raw.includes(',') && /\s+/.test(raw)) {
        const parts = raw.split(/\s+/);
        const apellido = parts.shift();
        const nombre = parts.join(' ');
        normalized = nombre ? `${apellido}, ${nombre}` : raw;
      } else {
        normalized = raw;
      }
    }

    setDebouncedSearch(normalized);
    setPage(1);
  };

  const endpoint = `/api/asociacion?page=${page}&limit=${tablePageSize > 0 ? tablePageSize : 1000
    }&search=${encodeURIComponent(debouncedSearch)}`;

  const { data: response, loading, error } = useApi(endpoint);

  const waLimitParam = waPageSize === 'all' ? 'all' : String(waPageSize);
  const waEndpoint = waPopupOpen
    ? `/api/asociacion/altas-whatsapp?page=${waPage}&limit=${encodeURIComponent(waLimitParam)}&desde=${encodeURIComponent(waApplied.desde)}&hasta=${encodeURIComponent(waApplied.hasta)}&search=${encodeURIComponent(waApplied.search || '')}`
    : null;
  const { data: waResponse, loading: waLoading, error: waError } = useApi(waEndpoint);
  const waCountEndpoint = waPopupOpen
    ? null
    : `/api/asociacion/altas-whatsapp?page=1&limit=1&desde=${encodeURIComponent(waApplied.desde)}&hasta=${encodeURIComponent(waApplied.hasta)}&search=${encodeURIComponent(waApplied.search || '')}`;
  const { data: waCountResponse, loading: waCountLoading } = useApi(waCountEndpoint);
  const waRows = useMemo(
    () => (waResponse?.data || []).map((r, idx) => ({
      ...r,
      id: r.id_unico ?? `wa-${r.dni_normalizado || 'row'}-${idx}`,
      sin_plex_geclisa: r.sin_plex_geclisa === 1
        || r.sin_plex_geclisa === true
        || r.origen_alta === 'externo_n8n',
      pendiente_ficha_geclisa: r.pendiente_ficha_geclisa === 1
        || r.pendiente_ficha_geclisa === true
        || r.origen_alta === 'geclisa_whatsapp'
    })),
    [waResponse]
  );
  const waTotal = waResponse?.total || 0;
  const waSinPlexTotal = Number(waResponse?.sin_plex_total) || 0;
  const waTriggerCount = waPopupOpen ? waTotal : (Number(waCountResponse?.total) || 0);
  const waTriggerCountLoading = waPopupOpen ? waLoading : waCountLoading;
  const waTriggerSinPlex = waPopupOpen
    ? waSinPlexTotal
    : (Number(waCountResponse?.sin_plex_total) || 0);
  const waEffectiveLimit = waPageSize === 'all' ? waTotal || 1 : Number(waPageSize) || 10;
  const waTotalPages = waPageSize === 'all' ? 1 : Math.max(1, Math.ceil(waTotal / waEffectiveLimit));
  const waPaginationLabel = useMemo(() => {
    if (!waTotal) return 'Sin registros';
    if (waPageSize === 'all') return `Mostrando los ${waTotal} registros`;
    const from = (waPage - 1) * waEffectiveLimit + 1;
    const to = Math.min(waPage * waEffectiveLimit, waTotal);
    return `Mostrando ${from}–${to} de ${waTotal}`;
  }, [waPage, waPageSize, waTotal, waEffectiveLimit]);

  const waPlexEndpoint = waPlexPopupOpen
    ? `/api/atencion-bot/altas-whatsapp-plex?page=${waPlexPage}&limit=${waPlexPageSize}&desde=${encodeURIComponent(waPlexApplied.desde)}&hasta=${encodeURIComponent(waPlexApplied.hasta)}&search=${encodeURIComponent(waPlexApplied.search || '')}`
    : null;
  const { data: waPlexResponse, loading: waPlexLoading, error: waPlexError } = useApi(waPlexEndpoint);
  const waPlexRows = waPlexResponse?.data || [];
  const waPlexTotal = waPlexResponse?.total || 0;
  const waPlexTotalPages = Math.max(1, Math.ceil(waPlexTotal / waPlexPageSize));

  const dniForRecetas = selectedCliente?.dni_normalizado
    ? String(selectedCliente.dni_normalizado || '').trim().replace(/\D/g, '').replace(/^0+/, '') || selectedCliente.dni_normalizado
    : null;
  /* Recetas (sin facturación; se hará aparte después) */
  const recetasEndpoint =
    dniForRecetas
      ? `/api/asociacion-recetas/y-facturacion?search=${encodeURIComponent(dniForRecetas)}&limitRecetas=200`
      : null;

  const { data: recetasResponse, loading: recetasLoading, error: recetasError } = useApi(recetasEndpoint);

  /* Historial completo (farmacia + sanatorio) ordenado de más antiguo a más reciente */
  const historialEndpoint = selectedCliente
    ? dniForRecetas
      ? `/api/asociacion/historial?dni=${encodeURIComponent(dniForRecetas)}`
      : `/api/asociacion/historial?${[
        selectedCliente.codcliente != null ? `codcliente=${encodeURIComponent(selectedCliente.codcliente)}` : '',
        selectedCliente.ficha_id != null ? `ficha_id=${encodeURIComponent(selectedCliente.ficha_id)}` : ''
      ].filter(Boolean).join('&')}`
    : null;

  const { data: historialResponse, loading: historialLoading } = useApi(historialEndpoint);

  const selectedClienteId = selectedCliente?.codcliente != null && Number.isFinite(Number(selectedCliente.codcliente))
    ? Number(selectedCliente.codcliente)
    : null;

  const vinculosEndpoint = selectedClienteId
    ? `/api/asociacion-recetas/clientes-vinculos/${selectedClienteId}?_r=${vinculosReloadKey}`
    : null;
  const { data: vinculosResponse, loading: vinculosLoading } = useApi(vinculosEndpoint);

  const vinculoTiposEndpoint = profileTab === 'vinculos'
    ? '/api/asociacion-recetas/vinculos-tipos'
    : null;
  const { data: vinculoTiposResponse } = useApi(vinculoTiposEndpoint);

  const vinculoSearchNormalized = String(vinculoSearch || '').trim();
  const vinculosSearchEndpoint = (profileTab === 'vinculos' && vinculoSearchNormalized)
    ? `/api/asociacion-recetas/clientes-lookup?q=${encodeURIComponent(vinculoSearchNormalized)}&limit=20`
    : null;
  const { data: vinculoSearchResponse, loading: vinculoSearchLoading } = useApi(vinculosSearchEndpoint);

  const recetas = recetasResponse?.recetas?.data ?? [];
  const itemsPlex = recetasResponse?.recetas?.itemsPlex ?? [];
  const itemsGeclisa = recetasResponse?.recetas?.itemsGeclisa ?? [];

  const toggleResumenAccordion = useCallback((sectionId) => {
    setResumenAccordion((prev) => (prev === sectionId ? null : sectionId));
  }, []);

  const toggleRecetasAccordion = useCallback((sectionId) => {
    setRecetasAccordion(sectionId);
  }, []);

  useEffect(() => {
    setResumenAccordion('datos');
  }, [selectedCliente?.codcliente, selectedCliente?.ficha_id]);

  useEffect(() => {
    setRecetasAccordion('farm');
  }, [selectedCliente?.codcliente, selectedCliente?.ficha_id]);

  const clientes = response?.data || [];
  const total = response?.total || 0;
  const stats = response?.stats || {};

  const effectivePageSize = tablePageSize > 0 ? tablePageSize : (total || PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));

  useEffect(() => {
    let openWa = false;
    let waSearch = '';
    try {
      openWa = sessionStorage.getItem(PULSE_CMD_OPEN_WA_KEY) === '1';
      waSearch = String(sessionStorage.getItem(PULSE_CMD_WA_SEARCH_KEY) || '').trim();
      if (openWa) {
        sessionStorage.removeItem(PULSE_CMD_OPEN_WA_KEY);
        sessionStorage.removeItem(PULSE_CMD_WA_SEARCH_KEY);
      }
    } catch {
      openWa = false;
    }
    if (!openWa) return;
    const range = defaultWaDateRange();
    setWaDesde(range.desde);
    setWaHasta(range.hasta);
    setWaSearch(waSearch);
    setWaPage(1);
    setWaApplied({ desde: range.desde, hasta: range.hasta, search: waSearch });
    setWaPopupOpen(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const rawDni = String(params.get('dni') || '').replace(/\D/g, '').replace(/^0+/, '');
    if (!rawDni) return;
    // If a profile is already open, close it first so deep-link can open the requested DNI.
    setSelectedCliente(null);
    setSearch(rawDni);
    setDebouncedSearch(rawDni);
    setPage(1);
    setPendingOpenDni(rawDni);
  }, [location.search]);

  const PREFETCH_MAX_PAGES = 5;
  useEffect(() => {
    if (!response || !totalPages || totalPages <= 1) return;
    if (selectedCliente) return;

    const endpoints = [];
    const lastPrefetch = Math.min(totalPages, PREFETCH_MAX_PAGES + 1);
    for (let p = 2; p <= lastPrefetch; p += 1) {
      const ep = `/api/asociacion?page=${p}&limit=${tablePageSize > 0 ? tablePageSize : 1000
        }&search=${encodeURIComponent(
          debouncedSearch
        )}`;
      endpoints.push(ep);
    }

    if (endpoints.length === 0) return;

    prefetchApi(endpoints).catch(() => { });
  }, [response, totalPages, debouncedSearch, selectedCliente, tablePageSize]);

  const kpis = useMemo(
    () => [
      {
        id: 'total',
        tone: 'total',
        label: 'Registros',
        shortLabel: 'Registros',
        value: total,
        hint: debouncedSearch ? 'Coinciden con la búsqueda' : 'Total en la tabla'
      },
      {
        id: 'cruce',
        tone: 'cruce',
        label: 'Cruzados PLEX + GECLISA',
        shortLabel: 'Cruzados',
        value: stats.totalCruzados ?? 0,
        hint: 'Tienen codcliente y ficha_id'
      },
      {
        id: 'farm',
        tone: 'farm',
        label: 'Último movimiento: farmacia',
        shortLabel: 'Últ. farmacia',
        value: stats.ultFarmacia ?? 0,
        hint: 'Última interacción en Farma GSA'
      },
      {
        id: 'san',
        tone: 'san',
        label: 'Último movimiento: sanatorio',
        shortLabel: 'Últ. sanatorio',
        value: stats.ultSanatorio ?? 0,
        hint: 'Última atención en sanatorio'
      }
    ],
    [total, debouncedSearch, stats]
  );

  const rows = useMemo(() => {
    if (!clientes || clientes.length === 0) return [];

    const groups = new Map();

    clientes.forEach(row => {
      const apellido = (row.apellido_geclisa || '').trim();
      const nombre = (row.nombre_geclisa || '').trim();

      let key;
      if (row.dni_normalizado) {
        key = `dni:${row.dni_normalizado}`;
      } else if (row.codcliente != null) {
        key = `plex:${row.codcliente}`;
      } else if (row.ficha_id != null) {
        key = `gec:${row.ficha_id}`;
      } else if (row.id_unico) {
        key = `id:${row.id_unico}`;
      } else {
        key = `name:${apellido}|${nombre}`;
      }

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(row);
    });

    const grouped = Array.from(groups.values()).map(group => {
      const base = group[0];

      const movimientosRaw = [];
      group.forEach(r => {
        if (r.fecha_ult_compra) {
          movimientosRaw.push({
            origin: 'farmacia',
            tipo: 'Farmacia',
            date: r.fecha_ult_compra
          });
        }
        if (r.fecha_ult_atencion) {
          movimientosRaw.push({
            origin: 'sanatorio',
            tipo: 'Sanatorio',
            date: r.fecha_ult_atencion
          });
        }
      });

      // De-dupe por (origen + fecha) para evitar repeticiones del mismo movimiento
      const seenMov = new Set();
      const movimientos = [];
      movimientosRaw.forEach(m => {
        const key = `${m.origin}|${String(m.date)}`;
        if (seenMov.has(key)) return;
        seenMov.add(key);
        movimientos.push(m);
      });

      movimientos.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      const contactosGeclisa = uniqueNonEmpty(
        group.flatMap(r => [r.telefono_geclisa, r.celular_geclisa])
      );
      const contactosPlex = uniqueNonEmpty(group.map(r => r.telefono_plex));
      const emailsGeclisa = uniqueNonEmpty(group.map(r => r.email_geclisa));
      const emailsPlex = uniqueNonEmpty(group.map(r => r.email_plex));

      const latestFarmacia = movimientos.find(m => m.origin === 'farmacia');
      const latestSanatorio = movimientos.find(m => m.origin === 'sanatorio');

      return {
        ...base,
        id:
          base.id_unico ??
          `${base.dni_normalizado || ''}-${base.codcliente || ''}-${base.ficha_id || ''}`,
        fecha_ult_compra: latestFarmacia?.date ?? base.fecha_ult_compra,
        fecha_ult_atencion: latestSanatorio?.date ?? base.fecha_ult_atencion,
        __movimientos: movimientos,
        __contactosGeclisa: contactosGeclisa,
        __contactosPlex: contactosPlex,
        __emailsGeclisa: emailsGeclisa,
        __emailsPlex: emailsPlex,
        __allRows: group
      };
    });

    return grouped;
  }, [clientes]);

  useEffect(() => {
    if (!pendingOpenDni || selectedCliente) return;
    const match = rows.find((r) => {
      const rowDni = String(r?.dni_normalizado || '').replace(/\D/g, '').replace(/^0+/, '');
      return rowDni && rowDni === pendingOpenDni;
    });
    if (!match) return;
    setSelectedCliente(match);
    const params = new URLSearchParams(location.search || '');
    const requestedTab = String(params.get('tab') || '').trim().toLowerCase();
    const allowedTabs = new Set(['resumen', 'historial', 'recetas', 'vinculos']);
    setProfileTab(allowedTabs.has(requestedTab) ? requestedTab : 'resumen');
    setHistFilter('all');
    setPendingOpenDni('');
  }, [pendingOpenDni, rows, selectedCliente, location.search]);

  const vinculos = useMemo(() => {
    const arr = Array.isArray(vinculosResponse?.vinculos) ? vinculosResponse.vinculos : [];
    return arr;
  }, [vinculosResponse]);

  const vinculoCandidates = useMemo(() => {
    const arr = Array.isArray(vinculoSearchResponse?.data) ? vinculoSearchResponse.data : [];
    return arr
      .filter((r) => Number.isFinite(Number(r?.codcliente)))
      .filter((r) => Number(r.codcliente) !== Number(selectedClienteId));
  }, [vinculoSearchResponse, selectedClienteId]);

  const vinculoTipos = useMemo(() => {
    const fromDb = Array.isArray(vinculoTiposResponse?.tipos)
      ? vinculoTiposResponse.tipos.map((t) => String(t?.codigo || '').trim()).filter(Boolean)
      : [];
    return fromDb.length ? fromDb : VINCULO_TIPOS_DEFAULT;
  }, [vinculoTiposResponse]);

  useEffect(() => {
    if (!vinculoTipos.length) return;
    if (selectedVinculoTipo && !vinculoTipos.includes(selectedVinculoTipo)) {
      setSelectedVinculoTipo('');
    }
  }, [vinculoTipos, selectedVinculoTipo]);

  async function guardarVinculo() {
    setVinculoFeedback('');
    if (!selectedClienteId) {
      setVinculoFeedback('No hay cliente titular seleccionado');
      return;
    }
    const familiarId = Number(selectedVinculoClienteId);
    if (!Number.isFinite(familiarId) || familiarId <= 0) {
      setVinculoFeedback('Seleccioná un familiar válido');
      return;
    }
    if (!selectedVinculoTipo) {
      setVinculoFeedback('Seleccioná un tipo de vínculo');
      return;
    }
    setVinculoSaving(true);
    try {
      const selectedCand = vinculoCandidates.find((c) => Number(c?.codcliente) === Number(familiarId)) || null;
      const titularDni = String(selectedCliente?.dni_normalizado || '').replace(/\D/g, '').replace(/^0+/, '');
      const familiarDni = String(selectedCand?.dni_normalizado || '').replace(/\D/g, '').replace(/^0+/, '');

      let res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/clientes-vinculos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_dni: titularDni || null,
          cliente_familiar_dni: familiarDni || null,
          vinculo: selectedVinculoTipo
        })
      });

      // Compatibilidad: si backend levantado no tiene aún la ruta nueva, usar endpoint legado n8n.
      if (res.status === 404) {
        res = await fetch('/api/asociacion-recetas/vinculos-clientes', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cliente_principal_dni: titularDni || null,
            data: [
              {
                familiar_dni: familiarDni || null,
                tipo_vinculo: selectedVinculoTipo,
                familiar_id: familiarId,
                nombre_completo: selectedCand ? getDisplayName(selectedCand) : null,
                obra_social: selectedCand?.obra_social_descripcio || null
              }
            ]
          })
        });
      }

      const json = await res.json().catch(() => ({}));
      if (res.status === 401) {
        throw new Error('Sesión expirada. Volvé a iniciar sesión');
      }
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      setVinculoFeedback('Vínculo guardado correctamente');
      showToast('Vínculo guardado correctamente.', 'success');
      setSelectedVinculoClienteId('');
      setVinculoSearch('');
      setVinculosReloadKey((v) => v + 1);
    } catch (e) {
      const msg = `No se pudo guardar vínculo: ${e.message || 'error'}`;
      setVinculoFeedback(msg);
      showToast(msg, 'error');
    } finally {
      setVinculoSaving(false);
    }
  }

  async function guardarEdicionVinculo(vinculoId) {
    setVinculoFeedback('');
    if (!Number.isFinite(Number(vinculoId)) || Number(vinculoId) <= 0) {
      setVinculoFeedback('Vínculo inválido para editar');
      return;
    }
    if (!editingVinculoTipo) {
      setVinculoFeedback('Seleccioná un tipo de vínculo');
      return;
    }
    setVinculoSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/clientes-vinculos/${vinculoId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vinculo: editingVinculoTipo })
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error('Sesión expirada. Volvé a iniciar sesión');
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      setVinculoFeedback('');
      showToast('Vínculo editado correctamente.', 'success');
      setEditingVinculoId(null);
      setEditingVinculoTipo('');
      setVinculosReloadKey((v) => v + 1);
    } catch (e) {
      const msg = `No se pudo editar vínculo: ${e.message || 'error'}`;
      setVinculoFeedback(msg);
      showToast(msg, 'error');
    } finally {
      setVinculoSaving(false);
    }
  }

  const timelineEvents = useMemo(() => {
    if (!selectedCliente) return [];

    const recetasList = recetas || [];
    const itemsG = itemsGeclisa || [];
    const itemsP = itemsPlex || [];

    const toDayStart = (dateVal) => {
      const d = new Date(dateVal);
      if (Number.isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };

    const getDescFromRecetas = (eventDate, origin) => {
      const day = toDayStart(eventDate);
      if (day == null) return null;
      if (origin === 'sanatorio') {
        const recIds = recetasList
          .filter((r) => r.rec_fecha_geclisa && toDayStart(r.rec_fecha_geclisa) === day)
          .map((r) => r.rec_enca_id_geclisa)
          .filter((id) => id != null);
        const labels = itemsG
          .filter((it) => recIds.includes(it.rec_enca_id))
          .map((it) => (it.generico || it.presentacion || it.descripcion || it.medicamento || '').toString().trim())
          .filter(Boolean);
        const uniq = [...new Set(labels)];
        return uniq.length > 0 ? uniq.join(', ') : null;
      }
      if (origin === 'farmacia') {
        const recIds = recetasList
          .filter((r) => r.fechaemision_plex && toDayStart(r.fechaemision_plex) === day)
          .map((r) => r.idreceta_plex)
          .filter((id) => id != null);
        const labels = itemsP
          .filter((it) => recIds.includes(it.idreceta))
          .map((it) => (it.producto || it.presentaci || it.descripcion || it.medicamento || it.nombre || '').toString().trim())
          .filter(Boolean);
        const uniq = [...new Set(labels)];
        return uniq.length > 0 ? uniq.join(', ') : null;
      }
      return null;
    };

    const defaultDesc = (origin) =>
      origin === 'farmacia' ? 'Movimiento en farmacia' : 'Movimiento en sanatorio';

    const getSvc = (origin) =>
      origin === 'farmacia'
        ? 'Farmacia'
        : (selectedCliente.servicio_nombre_geclisa?.trim() || 'Sanatorio');

    const events = [];
    const historial = historialResponse?.historial;

    if (Array.isArray(historial) && historial.length > 0) {
      historial.forEach(m => {
        const descFromRecetas = getDescFromRecetas(m.date, m.origin);
        const baseDesc = defaultDesc(m.origin);
        events.push({
          date: m.date,
          tipo: m.origin === 'farmacia' ? 'Farmacia' : 'Sanatorio',
          origin: m.origin,
          ico: m.origin === 'farmacia' ? '💊' : '🏥',
          prestador: m.origin === 'sanatorio' ? (m.prestador || selectedCliente?.prestador_geclisa || null) : null,
          svc: getSvc(m.origin),
          desc: descFromRecetas ?? baseDesc,
          note: descFromRecetas ? baseDesc : null
        });
      });
    } else {
      const sourceMovs = Array.isArray(selectedCliente.__movimientos)
        ? selectedCliente.__movimientos
        : null;

      if (sourceMovs && sourceMovs.length > 0) {
        sourceMovs.forEach(m => {
          const descFromRecetas = getDescFromRecetas(m.date, m.origin);
          const baseDesc = defaultDesc(m.origin);
          events.push({
            date: m.date,
            tipo: m.tipo,
            origin: m.origin,
            ico: m.origin === 'farmacia' ? '💊' : '🏥',
            prestador: m.origin === 'sanatorio' ? selectedCliente?.prestador_geclisa || null : null,
            svc: getSvc(m.origin),
            desc: descFromRecetas ?? baseDesc,
            note: descFromRecetas ? baseDesc : null
          });
        });
      } else {
        if (selectedCliente.fecha_ult_compra) {
          const descFromRecetas = getDescFromRecetas(selectedCliente.fecha_ult_compra, 'farmacia');
          const baseDesc = defaultDesc('farmacia');
          events.push({
            date: selectedCliente.fecha_ult_compra,
            tipo: 'Farmacia',
            origin: 'farmacia',
            ico: '💊',
            svc: getSvc('farmacia'),
            desc: descFromRecetas ?? baseDesc,
            note: descFromRecetas ? baseDesc : null
          });
        }
        if (selectedCliente.fecha_ult_atencion) {
          const descFromRecetas = getDescFromRecetas(selectedCliente.fecha_ult_atencion, 'sanatorio');
          const baseDesc = defaultDesc('sanatorio');
          events.push({
            date: selectedCliente.fecha_ult_atencion,
            tipo: 'Sanatorio',
            origin: 'sanatorio',
            ico: '🏥',
            svc: getSvc('sanatorio'),
            desc: descFromRecetas ?? baseDesc,
            note: descFromRecetas ? baseDesc : null,
            prestador: selectedCliente?.prestador_geclisa || null
          });
        }
      }
    }

    const seen = new Set();
    const deduped = [];
    events.forEach(ev => {
      const t = new Date(ev.date).getTime();
      const key = `${ev.origin}|${Number.isNaN(t) ? ev.date : t}|${ev.desc || ''}|${ev.prestador || ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(ev);
    });

    return deduped.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [selectedCliente, historialResponse, recetas, itemsGeclisa, itemsPlex]);

  const movCountOptions = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const set = new Set();
    rows.forEach(r => {
      const cant = (r.__movimientos || []).length;
      if (cant > 0) set.add(cant);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (movCountFilter == null) return rows;
    return rows.filter(r => (r.__movimientos || []).length === movCountFilter);
  }, [rows, movCountFilter]);

  const timelineSearched = useMemo(() => {
    const term = timelineSearch.trim().toLowerCase();
    if (!term) return timelineEvents;

    return timelineEvents.filter((ev) => {
      const text = [
        ev.tipo || '',
        ev.svc || '',
        ev.desc || '',
        ev.note || '',
        ev.prestador || '',
        formatDateTime(ev.date) || '',
        formatDate(ev.date) || ''
      ]
        .join(' ')
        .toLowerCase();
      return text.includes(term);
    });
  }, [timelineEvents, timelineSearch]);

  const timelineTotalPages = useMemo(
    () => {
      if (!timelinePageSize || timelinePageSize <= 0) return 1;
      return Math.max(1, Math.ceil((timelineSearched.length || 0) / timelinePageSize));
    },
    [timelineSearched, timelinePageSize]
  );

  const timelinePageItems = useMemo(() => {
    if (!timelineSearched || timelineSearched.length === 0) return [];
    if (!timelinePageSize || timelinePageSize <= 0) return timelineSearched;
    const start = (timelinePage - 1) * timelinePageSize;
    return timelineSearched.slice(start, start + timelinePageSize);
  }, [timelineSearched, timelinePage, timelinePageSize]);

  const historialFiltered = useMemo(() => {
    if (histFilter === 'all') return timelineEvents;
    return timelineEvents.filter((e) => e.origin === histFilter);
  }, [timelineEvents, histFilter]);

  const historialSearched = useMemo(() => {
    const term = histSearch.trim().toLowerCase();
    if (!term) return historialFiltered;

    return historialFiltered.filter((ev) => {
      const text = [
        ev.tipo || '',
        ev.svc || '',
        ev.desc || '',
        ev.note || '',
        formatDate(ev.date) || ''
      ]
        .join(' ')
        .toLowerCase();
      return text.includes(term);
    });
  }, [historialFiltered, histSearch]);

  const histTotalPages = useMemo(
    () => {
      if (!histPageSize || histPageSize <= 0) return 1;
      return Math.max(1, Math.ceil((historialSearched.length || 0) / histPageSize));
    },
    [historialSearched, histPageSize]
  );

  const historialPageItems = useMemo(() => {
    if (!historialSearched || historialSearched.length === 0) return [];
    if (!histPageSize || histPageSize <= 0) return historialSearched;
    const start = (histPage - 1) * histPageSize;
    return historialSearched.slice(start, start + histPageSize);
  }, [historialSearched, histPage, histPageSize]);

  useEffect(() => {
    setHistPage(1);
  }, [histFilter, histSearch, selectedCliente, histPageSize]);

  useEffect(() => {
    setTimelinePage(1);
    setTimelineSearch('');
    setHistSearch('');
    setHistFilter('all');
    setRecGeSearch('');
    setRecPlexSearch('');
    setRecetasAccordion('farm');
    setRecGePage(1);
    setRecPlexPage(1);
  }, [selectedCliente]);

  useEffect(() => {
    if (timelinePage > timelineTotalPages) {
      setTimelinePage(1);
    }
  }, [timelinePage, timelineTotalPages]);

  useEffect(() => {
    setTimelinePage(1);
  }, [timelinePageSize, timelineSearch]);

  useEffect(() => {
    if (histPage > histTotalPages) {
      setHistPage(1);
    }
  }, [histPage, histTotalPages]);

  useEffect(() => {
    setHistPage(1);
  }, [histPageSize]);

  const recetasGeclisa = useMemo(
    () => {
      // Filtrar recetas válidas y eliminar duplicados por rec_enca_id_geclisa
      const valid = recetas.filter(
        (r) => r.rec_fecha_geclisa != null || r.rec_enca_id_geclisa != null
      );
      const byId = new Map();
      valid.forEach((r) => {
        const id = r.rec_enca_id_geclisa;
        if (id != null) {
          if (!byId.has(id)) {
            byId.set(id, r);
          }
        } else {
          // Sin ID, las mantenemos tal cual (casos raros)
          byId.set(`__noid__${byId.size}`, r);
        }
      });
      return Array.from(byId.values());
    },
    [recetas]
  );
  const recetasPlex = useMemo(
    () => {
      // Filtrar recetas válidas y eliminar duplicados por número visible (numreceta_plex || idreceta_plex)
      const valid = recetas.filter(
        (r) => r.fechaemision_plex != null || r.idreceta_plex != null
      );
      const byVisibleNum = new Map();
      valid.forEach((r) => {
        const visibleNum = (r.numreceta_plex ?? r.idreceta_plex) ?? null;
        if (visibleNum != null) {
          if (!byVisibleNum.has(visibleNum)) {
            byVisibleNum.set(visibleNum, r);
          }
        } else {
          // Sin número visible, las mantenemos tal cual (casos raros)
          byVisibleNum.set(`__noid__${byVisibleNum.size}`, r);
        }
      });
      return Array.from(byVisibleNum.values());
    },
    [recetas]
  );

  const sortedRecetasGeclisa = useMemo(() => {
    const arr = [...recetasGeclisa];
    arr.sort((a, b) => {
      const ta = a.rec_fecha_geclisa ? new Date(a.rec_fecha_geclisa).getTime() : 0;
      const tb = b.rec_fecha_geclisa ? new Date(b.rec_fecha_geclisa).getTime() : 0;
      return tb - ta;
    });
    return arr;
  }, [recetasGeclisa]);

  const sortedRecetasPlex = useMemo(() => {
    const arr = [...recetasPlex];
    arr.sort((a, b) => {
      const ta = a.fechaemision_plex ? new Date(a.fechaemision_plex).getTime() : 0;
      const tb = b.fechaemision_plex ? new Date(b.fechaemision_plex).getTime() : 0;
      return tb - ta;
    });
    return arr;
  }, [recetasPlex]);

  const filteredRecetasGeclisa = useMemo(() => {
    const term = recGeSearch.trim().toLowerCase();
    if (!term) return sortedRecetasGeclisa;

    return sortedRecetasGeclisa.filter((r) => {
      const idReceta = r.rec_enca_id_geclisa;
      const itemsDeReceta = (itemsGeclisa || []).filter(
        (it) => it.rec_enca_id === idReceta
      );

      const baseMatches = Object.values(r || {}).some((v) =>
        (v ?? '').toString().toLowerCase().includes(term)
      );
      if (baseMatches) return true;

      return itemsDeReceta.some((it) =>
        Object.values(it || {}).some((v) =>
          (v ?? '').toString().toLowerCase().includes(term)
        )
      );
    });
  }, [sortedRecetasGeclisa, recGeSearch, itemsGeclisa]);

  const filteredRecetasPlex = useMemo(() => {
    const term = recPlexSearch.trim().toLowerCase();
    if (!term) return sortedRecetasPlex;

    return sortedRecetasPlex.filter((r) => {
      const idReceta = r.idreceta_plex;
      const itemsDeReceta = (itemsPlex || []).filter(
        (it) => it.idreceta === idReceta
      );

      const baseMatches = Object.values(r || {}).some((v) =>
        (v ?? '').toString().toLowerCase().includes(term)
      );
      if (baseMatches) return true;

      return itemsDeReceta.some((it) =>
        Object.values(it || {}).some((v) =>
          (v ?? '').toString().toLowerCase().includes(term)
        )
      );
    });
  }, [sortedRecetasPlex, recPlexSearch, itemsPlex]);

  const recGeTotalPages = useMemo(
    () => {
      if (!recGePageSize || recGePageSize <= 0) return 1;
      return Math.max(1, Math.ceil((filteredRecetasGeclisa.length || 0) / recGePageSize));
    },
    [filteredRecetasGeclisa, recGePageSize]
  );

  const recPlexTotalPages = useMemo(
    () => {
      if (!recPlexPageSize || recPlexPageSize <= 0) return 1;
      return Math.max(1, Math.ceil((filteredRecetasPlex.length || 0) / recPlexPageSize));
    },
    [filteredRecetasPlex, recPlexPageSize]
  );

  const recGePageItems = useMemo(() => {
    if (!filteredRecetasGeclisa || filteredRecetasGeclisa.length === 0) return [];
    if (!recGePageSize || recGePageSize <= 0) return filteredRecetasGeclisa;
    const start = (recGePage - 1) * recGePageSize;
    return filteredRecetasGeclisa.slice(start, start + recGePageSize);
  }, [filteredRecetasGeclisa, recGePage, recGePageSize]);

  const recPlexPageItems = useMemo(() => {
    if (!filteredRecetasPlex || filteredRecetasPlex.length === 0) return [];
    if (!recPlexPageSize || recPlexPageSize <= 0) return filteredRecetasPlex;
    const start = (recPlexPage - 1) * recPlexPageSize;
    return filteredRecetasPlex.slice(start, start + recPlexPageSize);
  }, [filteredRecetasPlex, recPlexPage, recPlexPageSize]);

  useEffect(() => {
    if (recGePage > recGeTotalPages) {
      setRecGePage(1);
    }
  }, [recGePage, recGeTotalPages]);

  useEffect(() => {
    if (recPlexPage > recPlexTotalPages) {
      setRecPlexPage(1);
    }
  }, [recPlexPage, recPlexTotalPages]);

  useEffect(() => {
    setRecGePage(1);
  }, [recGePageSize]);

  useEffect(() => {
    setRecPlexPage(1);
  }, [recPlexPageSize]);

  useEffect(() => {
    setRecGePage(1);
  }, [recGeSearch]);

  useEffect(() => {
    setRecPlexPage(1);
  }, [recPlexSearch]);

  const medicamentos = useMemo(() => {
    const medsMap = new Map();

    const addMed = (nameRaw, source) => {
      const name = (nameRaw || '').toString().trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!medsMap.has(key)) {
        medsMap.set(key, { label: name, sources: new Set([source]), count: 1 });
      } else {
        const entry = medsMap.get(key);
        entry.sources.add(source);
        entry.count += 1;
      }
    };

    (itemsGeclisa || []).forEach((it) => {
      const name =
        it.generico ||
        it.presentacion ||
        it.descripcion ||
        it.medicamento;
      addMed(name, 'GECLISA');
    });

    (itemsPlex || []).forEach((it) => {
      const name =
        it.producto ||
        it.presentaci ||
        it.descripcion ||
        it.medicamento ||
        it.nombre;
      addMed(name, 'PLEX');
    });

    const result = Array.from(medsMap.values()).map((m) => ({
      label: m.label,
      sources: Array.from(m.sources).sort(),
      count: m.count
    }));

    // Fallback: si hay ítems o recetas pero no se pudo resolver nombre, mostrar un tag genérico
    if (
      result.length === 0 &&
      (
        (itemsGeclisa && itemsGeclisa.length) ||
        (itemsPlex && itemsPlex.length) ||
        (recetas && recetas.length)
      )
    ) {
      return [{
        label: 'Medicamentos en recetas',
        sources: [],
        count: 0
      }];
    }

    return result;
  }, [itemsGeclisa, itemsPlex, recetas]);

  const filteredMedicamentos = useMemo(() => {
    const term = medSearch.trim().toLowerCase();
    if (!term) return medicamentos;
    return medicamentos.filter((m) =>
      m.label.toLowerCase().includes(term)
    );
  }, [medicamentos, medSearch]);

  /** Ítems PLEX agrupados por idreceta para mostrar bajo cada receta */
  const itemsPlexByReceta = useMemo(() => {
    const map = new Map();
    (itemsPlex || []).forEach((it) => {
      const id = it.idreceta;
      if (id != null) {
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(it);
      }
    });
    return map;
  }, [itemsPlex]);

  /** Ítems GECLISA agrupados por rec_enca_id para mostrar bajo cada receta */
  const itemsGeclisaByReceta = useMemo(() => {
    const map = new Map();
    (itemsGeclisa || []).forEach((it) => {
      const id = it.rec_enca_id;
      if (id != null) {
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(it);
      }
    });
    return map;
  }, [itemsGeclisa]);

  function formatCobertura(value) {
    const clean = (value || '').toString().trim().toLowerCase();
    if (!clean) return '';
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  }

  function formatDaysFromNowMeta(date) {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;

    const now = new Date();
    // Normalizar a medianoche para contar días calendario
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfThatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const diffMs = startOfToday.getTime() - startOfThatDay.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    let label;
    if (diffDays === 0) label = 'Hoy';
    else if (diffDays === 1) label = 'Hace 1 día';
    else if (diffDays > 1) label = `Hace ${diffDays} días`;
    else {
      const abs = Math.abs(diffDays);
      label = abs === 1 ? 'En 1 día' : `En ${abs} días`;
    }

    // Colores según rango de días (solo para fechas pasadas)
    let color = '#7C98B6'; // default
    if (diffDays >= 100) {
      color = '#F2545B';
    } else if (diffDays >= 30) {
      color = '#F59E0B';
    }

    return { label, color };
  }

  const renderListActivity = (date) => {
    if (!date) {
      return (
        <div className="crm-ac-list-activity">
          <span className="crm-ac-list-activity__empty">—</span>
        </div>
      );
    }
    const meta = formatDaysFromNowMeta(date);
    let relClass = '';
    if (meta?.color === '#F2545B') relClass = ' is-critical';
    else if (meta?.color === '#F59E0B') relClass = ' is-warn';

    return (
      <div className="crm-ac-list-activity">
        <time className="crm-ac-list-activity__when" dateTime={String(date)}>
          {formatDateTime24(date)}
        </time>
        {meta ? (
          <span className={`crm-ac-list-activity__rel${relClass}`}>{meta.label}</span>
        ) : null}
      </div>
    );
  };

  const openClienteProfile = (row) => {
    setSelectedCliente(row);
    setProfileTab('resumen');
    setHistFilter('all');
  };

  const openWaPopup = () => {
    const range = defaultWaDateRange();
    setWaDesde(range.desde);
    setWaHasta(range.hasta);
    setWaSearch('');
    setWaPage(1);
    setWaPageSize(10);
    setWaApplied({ desde: range.desde, hasta: range.hasta, search: '' });
    setWaPopupOpen(true);
  };

  useEffect(() => {
    if (!waPopupOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setWaPopupOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [waPopupOpen]);

  const openWaPlexPopup = () => {
    const range = defaultWaDateRange();
    setWaPlexDesde(range.desde);
    setWaPlexHasta(range.hasta);
    setWaPlexSearch('');
    setWaPlexPage(1);
    setWaPlexApplied({ desde: range.desde, hasta: range.hasta, search: '' });
    setWaPlexPopupOpen(true);
  };

  const applyWaFilters = () => {
    setWaPage(1);
    setWaApplied({
      desde: waDesde,
      hasta: waHasta,
      search: (waSearch || '').trim()
    });
  };

  const applyWaPlexFilters = () => {
    setWaPlexPage(1);
    setWaPlexApplied({
      desde: waPlexDesde,
      hasta: waPlexHasta,
      search: (waPlexSearch || '').trim()
    });
  };

  const openWaFicha = (row) => {
    if (!canOpenWaFicha(row)) return;
    const dniNorm = String(row.dni_normalizado || '')
      .replace(/\D/g, '')
      .replace(/^0+/, '');
    const fromList = rows.find((r) => {
      const rd = String(r?.dni_normalizado || '')
        .replace(/\D/g, '')
        .replace(/^0+/, '');
      return rd && rd === dniNorm;
    });
    const mapped = fromList || mapWaRowToCliente(row);
    if (!mapped) return;
    setWaPopupOpen(false);
    openClienteProfile(mapped);
  };

  const openWaPlexFicha = (row) => {
    if (!canOpenWaFicha(row)) return;
    const dniNorm = String(row.dni_normalizado || '')
      .replace(/\D/g, '')
      .replace(/^0+/, '');
    const fromList = rows.find((r) => {
      const rd = String(r?.dni_normalizado || '')
        .replace(/\D/g, '')
        .replace(/^0+/, '');
      return rd && rd === dniNorm;
    });
    const mapped = fromList || mapWaPlexRowToCliente(row);
    if (!mapped) return;
    setWaPlexPopupOpen(false);
    openClienteProfile(mapped);
  };

  const renderWaPatient = (r) => {
    const displayName = getDisplayName(r);
    const dniFmt = formatDni(r.dni_normalizado);
    const isExterno = r.origen_alta === 'externo_n8n';
    const sinPlex = Boolean(r.sin_plex_geclisa);
    const initials = displayName
      .split(' ')
      .map((x) => x[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

    return (
      <div className={`crm-ac-wa-patient${sinPlex ? ' crm-ac-wa-patient--alert' : ''}`}>
        <div className={`crm-ac-wa-patient__avatar${isExterno ? ' crm-ac-wa-patient__avatar--externo' : ''}${sinPlex ? ' crm-ac-wa-patient__avatar--alert' : ''}`} aria-hidden="true">
          {initials}
        </div>
        <div className="crm-ac-wa-patient__copy">
          <div className="crm-ac-wa-patient__name-row">
            <div className="crm-ac-wa-patient__name">{displayName}</div>
            {sinPlex ? (
              <span className="crm-ac-wa-patient__alert-tag" title="No existe en Plex/Geclisa — requiere alta manual">
                Agregar
              </span>
            ) : null}
          </div>
          {dniFmt ? <div className="crm-ac-wa-patient__dni">DNI {dniFmt}</div> : null}
          <div className="crm-ac-wa-patient__origin">
            {isExterno ? 'Alta manual / n8n' : 'Captura WhatsApp'}
          </div>
        </div>
      </div>
    );
  };

  const renderWaEstado = (r) => {
    if (r.sin_plex_geclisa) {
      const isFarmacia = String(r.origen_bot || '').trim().toUpperCase() === 'F';
      return (
        <span
          className="crm-ac-wa-alert-badge crm-ac-wa-alert-badge--plex-missing"
          title={
            isFarmacia
              ? 'Falta registrar en PLEX — Alta manual requerida'
              : 'Falta registrar en GECLISA — Alta manual requerida'
          }
        >
          <span className="crm-ac-wa-alert-badge__icon" aria-hidden>!</span>
          <span className="crm-ac-wa-alert-badge__label">
            {isFarmacia ? 'Sin PLEX' : 'Sin GECLISA'}
          </span>
        </span>
      );
    }
    return (
      <span
        className="crm-ac-wa-alert-badge crm-ac-wa-alert-badge--ficha"
        title="Pendiente ficha — Vincular en GECLISA"
      >
        <span className="crm-ac-wa-alert-badge__icon crm-ac-wa-alert-badge__icon--soft" aria-hidden>◎</span>
        <span className="crm-ac-wa-alert-badge__label">Pend. ficha</span>
      </span>
    );
  };

  const renderWaTelefono = (r) => {
    const tels = uniqueNonEmpty([r.telefono_plex, r.telefono_geclisa, r.celular_geclisa]);
    if (!tels.length) return <span className="crm-ac-list-activity__empty">—</span>;
    return (
      <div className="crm-ac-wa-tel">
        <span className="crm-ac-wa-tel__icon" aria-hidden>📞</span>
        <span className="crm-ac-wa-tel__value">{tels[0]}</span>
      </div>
    );
  };

  const renderWaCobertura = (r) => {
    const osRaw = (r.obra_social_descripcio || '').trim();
    const os = formatCobertura(osRaw);
    const nro = (r.os_sigla_geclisa || '').trim();
    if (!os && !nro) return <span className="crm-ac-list-activity__empty">—</span>;
    const isPami = /pami/i.test(osRaw) || /pami/i.test(os);
    return (
      <div className="crm-ac-wa-cobertura">
        {os ? (
          <span className={`crm-ac-wa-cobertura__pill${isPami ? ' crm-ac-wa-cobertura__pill--pami' : ''}`}>
            {os}
          </span>
        ) : null}
        {nro ? <span className="crm-ac-wa-cobertura__sub">Afiliado {nro}</span> : null}
      </div>
    );
  };

  const renderWaOrigen = (r) => {
    const isFarmacia = String(r.origen_bot || '').trim().toUpperCase() === 'F';
    return (
      <span
        className={`crm-ac-wa-origin${
          isFarmacia ? ' crm-ac-wa-origin--farmacia' : ' crm-ac-wa-origin--cronicos'
        }`}
        title={
          isFarmacia
            ? 'Registrado por el Bot de Farmacia'
            : 'Registrado por el Bot de Tratamientos Crónicos'
        }
      >
        <span className="crm-ac-wa-origin__glyph" aria-hidden>
          {isFarmacia ? '💊' : '♻️'}
        </span>
        <span className="crm-ac-wa-origin__label">
          {isFarmacia ? 'Farmacia' : 'Tratamientos Crónicos'}
        </span>
      </span>
    );
  };

  const renderWaPlexPatient = (r) => {
    const displayName = getWaPlexDisplayName(r);
    const dniFmt = formatDni(r.dni_normalizado || r.dni);
    const initials = displayName
      .split(' ')
      .map((x) => x[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

    return (
      <div className="crm-ac-wa-patient">
        <div className="crm-ac-wa-patient__avatar crm-ac-wa-patient__avatar--plex" aria-hidden="true">
          {initials}
        </div>
        <div className="crm-ac-wa-patient__copy">
          <div className="crm-ac-wa-patient__name">{displayName}</div>
          {dniFmt ? <div className="crm-ac-wa-patient__dni">DNI {dniFmt}</div> : null}
        </div>
      </div>
    );
  };

  const waColumns = [
    {
      key: 'persona',
      label: 'Paciente',
      width: '22%',
      render: (r) => renderWaPatient(r)
    },
    {
      key: 'origen',
      label: 'Origen Bot',
      width: '17%',
      render: (r) => renderWaOrigen(r)
    },
    {
      key: 'estado',
      label: 'Estado',
      width: '15%',
      render: (r) => renderWaEstado(r)
    },
    {
      key: 'alta',
      label: 'Alta digital',
      width: '12%',
      render: (r) => (
        <div className="crm-ac-wa-date">
          {formatDateAr(r.fecha_alta_sanatorio) ? (
            <>
              <span className={`crm-ac-wa-date__chip${r.origen_alta === 'externo_n8n' ? ' crm-ac-wa-date__chip--externo' : ''}`}>
                {r.origen_alta === 'externo_n8n' ? 'EXT' : 'WA'}
              </span>
              <time className="crm-ac-wa-date__time" dateTime={String(r.fecha_alta_sanatorio)}>
                {formatDateAr(r.fecha_alta_sanatorio)}
              </time>
            </>
          ) : (
            <span className="crm-ac-list-activity__empty">—</span>
          )}
        </div>
      )
    },
    {
      key: 'tel',
      label: 'Teléfono',
      width: '12%',
      render: (r) => renderWaTelefono(r)
    },
    {
      key: 'os',
      label: 'Cobertura',
      width: '14%',
      render: (r) => renderWaCobertura(r)
    },
    {
      key: 'acciones',
      label: '',
      width: '8%',
      align: 'right',
      render: (r) => {
        if (!canOpenWaFicha(r)) return <span className="crm-ac-list-activity__empty">—</span>;
        return (
          <button
            type="button"
            className="crm-ac-wa-ficha-btn"
            onClick={(e) => {
              e.stopPropagation();
              openWaFicha(r);
            }}
          >
            👁️
            {/* <span className="crm-ac-wa-ficha-btn__arrow" aria-hidden>→</span> */}
          </button>
        );
      }
    }
  ];

  const waPlexColumns = [
    {
      key: 'persona',
      label: 'Paciente',
      width: '24%',
      render: (r) => renderWaPlexPatient(r)
    },
    {
      key: 'alta',
      label: 'Alta WhatsApp',
      width: '14%',
      render: (r) => (
        <div className="crm-ac-wa-date">
          {formatDateAr(r.created_at) ? (
            <>
              <span className="crm-ac-wa-date__chip crm-ac-wa-date__chip--plex">PLEX</span>
              <time dateTime={String(r.created_at)}>
                {formatDateAr(r.created_at)}
              </time>
            </>
          ) : (
            <span className="crm-ac-list-activity__empty">—</span>
          )}
        </div>
      )
    },
    {
      key: 'tel',
      label: 'Teléfono',
      width: '14%',
      render: (r) => (
        <span className="crm-ac-wa-text">{(r.telefono || '').trim() || '—'}</span>
      )
    },
    {
      key: 'email',
      label: 'Email',
      width: '18%',
      render: (r) => (
        <span className="crm-ac-wa-text crm-ac-wa-text--wrap">
          {(r.email || '').trim() || '—'}
        </span>
      )
    },
    {
      key: 'os',
      label: 'Cobertura',
      width: '16%',
      render: (r) => {
        const os = formatCobertura((r.obra_social || '').trim());
        const nro = (r.nro_afiliado || '').trim();
        if (!os && !nro) return <span className="crm-ac-list-activity__empty">—</span>;
        return (
          <div className="crm-ac-wa-cobertura">
            {os ? <span className="crm-ac-wa-cobertura__main">{os}</span> : null}
            {nro ? <span className="crm-ac-wa-cobertura__sub">Af. {nro}</span> : null}
          </div>
        );
      }
    },
    {
      key: 'nac',
      label: 'Nacimiento',
      width: '10%',
      render: (r) => (
        <span className="crm-ac-wa-text">{(r.fecha_nacimiento || '').trim() || '—'}</span>
      )
    },
    {
      key: 'acciones',
      label: '',
      width: '10%',
      align: 'right',
      render: (r) => {
        if (!canOpenWaFicha(r)) return <span className="crm-ac-list-activity__empty">—</span>;
        return (
          <button
            type="button"
            className="crm-ac-wa-ficha-btn"
            onClick={(e) => {
              e.stopPropagation();
              openWaPlexFicha(r);
            }}
          >
            Ver ficha
          </button>
        );
      }
    }
  ];

  const columns = [
    {
      key: 'persona',
      label: 'Paciente / Cliente',
      width: '28%',
      render: r => {
        const apellido = (r.apellido_geclisa || '').trim();
        const nombre = (r.nombre_geclisa || '').trim();
        const nombreGeclisa = [apellido, nombre].filter(Boolean).join(', ');
        const nombrePlex = (r.nombre_plex || '').trim();
        const displayName = nombreGeclisa || nombrePlex || 'Sin nombre';
        const dniMeta = getDniMeta(r.dni_normalizado);
        const dni = dniMeta.formatted;
        const clienteBadge = resolveClienteStatusBadge(r);

        const initials = displayName
          .split(' ')
          .map(x => x[0])
          .filter(Boolean)
          .slice(0, 2)
          .join('')
          .toUpperCase() || '?';

        return (
          <div
            className="crm-ac-patient-cell crm-ac-list-patient"
            onClick={() => openClienteProfile(r)}
            role="button"
            tabIndex={0}
            title={`Abrir el contacto de ${displayName}`}
            aria-label={`Abrir el contacto de ${displayName}`}
            onKeyDown={(evt) => {
              if (evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault();
                openClienteProfile(r);
              }
            }}
          >
            <div className="crm-ac-list-patient__avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="crm-ac-list-patient__copy">
              <div className="crm-ac-list-patient__name">{displayName}</div>
              <div className="crm-ac-list-patient__meta">
                {dni ? (
                  <div className="crm-ac-list-patient__dni-row">
                    <span className="crm-ac-list-patient__dni">DNI {dni}</span>
                    {clienteBadge?.tone === 'warning' ? (
                      <CrmStatusBadgeFrom badge={clienteBadge} compact />
                    ) : null}
                  </div>
                ) : clienteBadge?.tone === 'warning' ? (
                  <CrmStatusBadgeFrom badge={clienteBadge} compact />
                ) : null}
                {nombrePlex ? (
                  <div className="crm-ac-list-patient__plex">Plex: {nombrePlex}</div>
                ) : null}
              </div>
              <span className="crm-ac-list-patient__cta">Ver contacto</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'cobertura',
      label: 'Cobertura',
      width: '16%',
      render: r => {
        const rawPlex = (r.obra_social_descripcio || '').trim();
        const rawGe = (r.os_sigla_geclisa || '').trim();
        const obraPlex = formatCobertura(rawPlex);
        const obraGe = formatCobertura(rawGe);

        return (
          <div className="crm-ac-list-cobertura">
            <span
              className={`crm-ac-list-cobertura__pill crm-ac-list-cobertura__pill--plex${rawPlex ? '' : ' is-empty'}`}
              title={obraPlex || 'Sin cobertura farmacia'}
            >
              <span className="crm-ac-list-cobertura__tag">Farm.</span>
              {obraPlex || 'Sin cobertura'}
            </span>
            <span
              className={`crm-ac-list-cobertura__pill crm-ac-list-cobertura__pill--gec${rawGe ? '' : ' is-empty'}`}
              title={obraGe || 'Sin cobertura sanatorio'}
            >
              <span className="crm-ac-list-cobertura__tag">San.</span>
              {obraGe || 'Sin cobertura'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'origen',
      label: 'Origen',
      width: '18%',
      render: r => {
        const hasPlex = r.codcliente != null;
        const hasGeclisa = r.ficha_id != null;

        return (
          <div className="crm-ac-list-origen">
            <span
              className={`crm-ac-list-origen__badge crm-ac-list-origen__badge--plex${hasPlex ? ' is-on' : ''}`}
            >
              PLEX
            </span>
            <span
              className={`crm-ac-list-origen__badge crm-ac-list-origen__badge--gec${hasGeclisa ? ' is-on' : ''}`}
            >
              GECLISA
            </span>
          </div>
        );
      }
    },
    {
      key: 'ult_plex',
      label: 'Últ. compra Farmacia',
      width: '18%',
      render: r => renderListActivity(r.fecha_ult_compra)
    },
    {
      key: 'ult_geclisa',
      label: 'Últ. práctica Sanatorio',
      width: '18%',
      render: r => renderListActivity(r.fecha_ult_atencion)
    },
  ];

  const extraColumns = [
    {
      key: 'contacto',
      label: 'Contacto',
      render: r => {
        const telsGe = r.__contactosGeclisa || [];
        const telsPlex = r.__contactosPlex || [];

        if (telsGe.length === 0 && telsPlex.length === 0) return '—';

        return (
          <div style={{ fontSize: 12, lineHeight: 1.25 }}>
            {telsGe.length > 0 && (
              <div>
                <strong style={{ color: '#516F90' }}>GECLISA:</strong>{' '}
                {telsGe.join(' · ')}
              </div>
            )}
            {telsPlex.length > 0 && (
              <div>
                <strong style={{ color: '#516F90' }}>PLEX:</strong>{' '}
                {telsPlex.join(' · ')}
              </div>
            )}
          </div>
        );
      }
    },

    {
      key: 'email',
      label: 'Email',
      render: r => {
        const emailsGe = r.__emailsGeclisa || [];
        const emailsPlex = r.__emailsPlex || [];

        if (emailsGe.length === 0 && emailsPlex.length === 0) return '—';

        return (
          <div style={{ fontSize: 12, lineHeight: 1.25 }}>
            {emailsGe.length > 0 && (
              <div>
                <strong style={{ color: '#516F90' }}>GECLISA:</strong>{' '}
                {emailsGe.join(' · ')}
              </div>
            )}
            {emailsPlex.length > 0 && (
              <div>
                <strong style={{ color: '#516F90' }}>PLEX:</strong>{' '}
                {emailsPlex.join(' · ')}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'ids',
      label: 'IDs',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div>
            <strong style={{ color: '#516F90' }}>codcliente PLEX:</strong>{' '}
            {r.codcliente ?? '—'}
          </div>
          <div>
            <strong style={{ color: '#516F90' }}>ficha_id GECLISA:</strong>{' '}
            {r.ficha_id ?? '—'}
          </div>
        </div>
      )
    },
    {
      key: 'altas',
      label: 'Altas',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div>
            <strong style={{ color: '#516F90' }}>Alta sanatorio:</strong>{' '}
            {formatDate(r.fecha_alta_sanatorio) || '—'}
          </div>
          <div>
            <strong style={{ color: '#516F90' }}>Alta farmacia:</strong>{' '}
            {formatDate(r.fecha_alta_farma) || '—'}
          </div>
        </div>
      )
    },
    {
      key: 'domicilio',
      label: 'Domicilio PLEX',
      render: r => r.domicilio_plex?.trim() || '—'
    }
  ];

  const handleBackToList = () => {
    setSelectedCliente(null);
  };

  if (error) {
    return (
      <CenteredMessage
        icon="🔗"
        title="No se pudo cargar la asociación de clientes"
        message={error}
      />
    );
  }

  /* ========== VISTA PERFIL (click en fila) ========== */
  if (selectedCliente) {
    const c = selectedCliente;
    const displayName = getDisplayName(c);
    const initials = getInitials(c);
    const pacienteForPrint = {
      iniciales: initials,
      nombre: displayName,
      meta: [
        formatDni(c.dni_normalizado) && `DNI ${formatDni(c.dni_normalizado)}`,
        c.codcliente != null && `PLEX #${c.codcliente}`,
        c.ficha_id != null && `GECLISA #${c.ficha_id}`
      ].filter(Boolean).join(' · '),
      dni: formatDni(c.dni_normalizado) || ''
    };
    const telefonoGe = (c.telefono_geclisa || c.celular_geclisa || '').trim();
    const telefonoPlex = (c.telefono_plex || '').trim();
    const normalizePhone = (v) => (v || '').toString().replace(/\D/g, '');
    const phoneMatch =
      telefonoGe &&
      telefonoPlex &&
      normalizePhone(telefonoGe) === normalizePhone(telefonoPlex);
    const emailGe = (c.email_geclisa || '').trim();
    const emailPlex = (c.email_plex || '').trim();
    const normalizeEmail = (v) => (v || '').toString().trim().toLowerCase();
    const emailMatch =
      emailGe &&
      emailPlex &&
      normalizeEmail(emailGe) === normalizeEmail(emailPlex);
    const meta = pacienteForPrint.meta;
    const sanatorioRecetasTitle = `Recetas emitidas — ${c.servicio_nombre_geclisa?.trim() || 'Sanatorio'}`;

    return (
      <section className="crm-gd-page crm-ac-page crm-ac-page--profile crm-pbody">
        <div className="crm-ptopbar">
          <button type="button" className="crm-back-btn" onClick={handleBackToList}>
            ← Contactos
          </button>
          <div className="crm-sep" />
          <div className="crm-p-ava-lg" style={{ background: '#33475B' }}>
            {initials}
          </div>
          <div>
            <div className="crm-p-hname">{displayName}</div>
            <div className="crm-p-hmeta">{meta || '—'}</div>
          </div>
        </div>

        <div className="crm-profile-tabs">
          <button
            type="button"
            className={`crm-ptab ${profileTab === 'resumen' ? 'active' : ''}`}
            onClick={() => setProfileTab('resumen')}
          >
            📋 Resumen
          </button>
          <button
            type="button"
            className={`crm-ptab ${profileTab === 'historial' ? 'active' : ''}`}
            onClick={() => setProfileTab('historial')}
          >
            📜 Historial
          </button>
          <button
            type="button"
            className={`crm-ptab ${profileTab === 'recetas' ? 'active' : ''}`}
            onClick={() => setProfileTab('recetas')}
          >
            💊 Recetas
          </button>
          <button
            type="button"
            className={`crm-ptab ${profileTab === 'vinculos' ? 'active' : ''}`}
            onClick={() => setProfileTab('vinculos')}
          >
            👥 Vínculos
          </button>
        </div>

        {/* Tab Resumen */}
        <div className={`crm-tab-panel ${profileTab === 'resumen' ? 'active' : ''}`}>
          <div className="crm-pbody">
            <div className="crm-pleft crm-ac-pleft">
              <div
                className={`crm-icard crm-ac-accordion${resumenAccordion === 'datos' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="crm-ac-accordion__trigger"
                  aria-expanded={resumenAccordion === 'datos'}
                  onClick={() => toggleResumenAccordion('datos')}
                >
                  <span className="crm-ac-accordion__title">Datos personales</span>
                  <span className="crm-ac-accordion__chev" aria-hidden="true" />
                </button>
                <div className="crm-ac-accordion__panel">
                  <div className="crm-ac-accordion__inner crm-icard-b">
                  <div className="crm-ir"><span className="crm-ik">Nombre</span><span className="crm-iv">{displayName}</span></div>
                  <div className="crm-ir">
                    <span className="crm-ik">Obra social Plex</span>
                    <span className="crm-iv">
                      {c.obra_social_descripcio?.trim() || '—'}
                    </span>
                  </div>
                  <div className="crm-ir">
                    <span className="crm-ik">Obra social Geclisa</span>
                    <span className="crm-iv">
                      {c.os_sigla_geclisa?.trim() || '—'}
                    </span>
                  </div>
                  <div className="crm-ir">
                    <span className="crm-ik">DNI</span>
                    <span className="crm-iv">
                      {formatDni(c.dni_normalizado) || '—'}
                      {(() => {
                        const meta = getDniMeta(c.dni_normalizado);
                        return meta.digits &&
                          meta.digits.length > 0 &&
                          meta.digits.length <= 6 ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              marginLeft: 8,
                              padding: '2px 6px',
                              borderRadius: 999,
                              background: '#fef9c3',
                              color: '#854d0e',
                              fontSize: 10,
                              fontWeight: 600,
                              border: '1px solid #facc15'
                            }}
                          >
                            ⚠ DNI Incompleto
                          </span>
                        ) : null;
                      })()}
                    </span>
                  </div>
                  {phoneMatch ? (
                    <div className="crm-ir">
                      <span className="crm-ik">Teléfono</span>
                      <span className="crm-iv">
                        {telefonoGe || telefonoPlex || '—'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginLeft: 8,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: '#dcfce7',
                            color: '#166534',
                            fontSize: 10.5,
                            fontWeight: 600,
                            border: '1px solid #4ade80'
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '999px',
                              background: '#22c55e',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 9
                            }}
                          >
                            ✓
                          </span>
                          Coincide
                        </span>
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="crm-ir"><span className="crm-ik">Tel. GECLISA</span><span className="crm-iv">{telefonoGe || '—'}</span></div>
                      <div className="crm-ir"><span className="crm-ik">Tel. PLEX</span><span className="crm-iv">{telefonoPlex || '—'}</span></div>
                    </>
                  )}
                  {emailMatch ? (
                    <div className="crm-ir">
                      <span className="crm-ik">Email</span>
                      <span className="crm-iv">
                        {emailGe || emailPlex || '—'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginLeft: 8,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: '#e0f2fe',
                            color: '#1d4ed8',
                            fontSize: 10.5,
                            fontWeight: 600,
                            border: '1px solid #60a5fa'
                          }}
                        >
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '999px',
                              background: '#22c55e',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 9
                            }}
                          >
                            ✓
                          </span>
                          Coincide
                        </span>
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="crm-ir"><span className="crm-ik">Email GECLISA</span><span className="crm-iv">{emailGe || '—'}</span></div>
                      <div className="crm-ir"><span className="crm-ik">Email PLEX</span><span className="crm-iv">{emailPlex || '—'}</span></div>
                    </>
                  )}
                  <div className="crm-ir"><span className="crm-ik">Alta sanatorio</span><span className="crm-iv">{formatDate(c.fecha_alta_sanatorio) || '—'}</span></div>
                  <div className="crm-ir"><span className="crm-ik">Alta farmacia</span><span className="crm-iv">{formatDate(c.fecha_alta_farma) || '—'}</span></div>
                  </div>
                </div>
              </div>

              <div
                className={`crm-icard crm-ac-accordion${resumenAccordion === 'origen' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="crm-ac-accordion__trigger"
                  aria-expanded={resumenAccordion === 'origen'}
                  onClick={() => toggleResumenAccordion('origen')}
                >
                  <span className="crm-ac-accordion__title">Origen</span>
                  <span className="crm-ac-accordion__chev" aria-hidden="true" />
                </button>
                <div className="crm-ac-accordion__panel">
                  <div className="crm-ac-accordion__inner crm-icard-b">
                    <div className="crm-smap">
                      <span className={`crm-sp ${c.codcliente != null ? 'crm-sp-u' : 'crm-sp-n'}`}>PLEX</span>
                      <span className={`crm-sp ${c.ficha_id != null ? 'crm-sp-u' : 'crm-sp-n'}`}>GECLISA</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`crm-icard crm-ac-accordion${resumenAccordion === 'mapa' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="crm-ac-accordion__trigger"
                  aria-expanded={resumenAccordion === 'mapa'}
                  onClick={() => toggleResumenAccordion('mapa')}
                >
                  <span className="crm-ac-accordion__title">Mapa de servicios</span>
                  <span className="crm-ac-accordion__chev" aria-hidden="true" />
                </button>
                <div className="crm-ac-accordion__panel">
                  <div className="crm-ac-accordion__inner crm-icard-b">
                    <div className="crm-smap">
                      {c.servicio_nombre_geclisa ? (
                        <span className="crm-sp crm-sp-u">{c.servicio_nombre_geclisa}</span>
                      ) : (
                        <span className="crm-sp crm-sp-n">Sin servicio</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`crm-icard crm-ac-accordion${resumenAccordion === 'meds' ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="crm-ac-accordion__trigger"
                  aria-expanded={resumenAccordion === 'meds'}
                  onClick={() => toggleResumenAccordion('meds')}
                >
                  <span className="crm-ac-accordion__title">Medicaciones</span>
                  <span className="crm-ac-accordion__chev" aria-hidden="true" />
                </button>
                <div className="crm-ac-accordion__panel">
                  <div className="crm-ac-accordion__inner crm-icard-b">
                    <div className="crm-ac-med-search">
                      <input
                        type="text"
                        className="crm-ac-med-search__input"
                        placeholder="Buscar medicamento…"
                        value={medSearch}
                        onChange={(e) => setMedSearch(e.target.value)}
                        aria-label="Buscar medicamento"
                      />
                      {medSearch.trim() ? (
                        <button
                          type="button"
                          className="crm-ac-med-search__clear"
                          onClick={() => setMedSearch('')}
                          aria-label="Limpiar búsqueda de medicamento"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                    {filteredMedicamentos.length === 0 ? (
                      <div
                        className={`crm-ac-med-empty${
                          medSearch.trim() ? ' crm-ac-med-empty--no-match' : ' crm-ac-med-empty--no-data'
                        }`}
                        role="status"
                      >
                        <span className="crm-ac-med-empty__icon" aria-hidden="true">
                          {medSearch.trim() ? '🔎' : '💊'}
                        </span>
                        {medSearch.trim() ? (
                          <>
                            <p className="crm-ac-med-empty__title">Sin coincidencias</p>
                            <p className="crm-ac-med-empty__term" title={medSearch.trim()}>
                              {medSearch.trim().length > 28
                                ? `${medSearch.trim().slice(0, 28)}…`
                                : medSearch.trim()}
                            </p>
                            <p className="crm-ac-med-empty__hint">
                              Probá con otro nombre, droga o marca del medicamento.
                            </p>
                            <button
                              type="button"
                              className="crm-ac-med-empty__action"
                              onClick={() => setMedSearch('')}
                            >
                              Limpiar búsqueda
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="crm-ac-med-empty__title">Sin medicaciones</p>
                            <p className="crm-ac-med-empty__hint">
                              No hay medicamentos registrados en recetas para este paciente.
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <ul className="crm-med-list crm-ac-med-list">
                        {filteredMedicamentos.map((m) => (
                          <li key={m.label} className="crm-med-list-item">
                            <span className="crm-med-list-label">
                              {m.label}
                              {m.sources.length > 0 && (
                                <span className="crm-med-tag-source">
                                  {' '}
                                  {m.sources.join(' · ')}
                                </span>
                              )}
                            </span>
                            <span className="crm-med-list-count-wrap">
                              <span className="crm-med-list-count-ball">{m.count}x</span>
                              <span className="crm-med-list-count-dot" />
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="crm-pright crm-ac-historial">
              <div className="crm-ac-timeline__toolbar">
                <div className="crm-ac-timeline__head">
                  <h3 className="crm-ac-timeline__title">Historial — Farmacia + Sanatorio</h3>
                  {timelineEvents.length > 0 ? (
                    <span
                      className={`crm-ac-timeline__count${
                        timelineSearch.trim() ? ' crm-ac-timeline__count--filtered' : ''
                      }`}
                    >
                      {timelineSearch.trim() ? (
                        <>
                          {timelineSearched.length} de {timelineEvents.length}
                        </>
                      ) : (
                        <>
                          {timelineEvents.length} movimiento{timelineEvents.length === 1 ? '' : 's'}
                        </>
                      )}
                    </span>
                  ) : null}
                </div>
                {timelineEvents.length > 0 ? (
                  <>
                    <div className="crm-ac-timeline__search">
                      <span className="crm-ac-timeline__search-icon" aria-hidden="true">
                        🔎
                      </span>
                      <input
                        type="search"
                        className="crm-ac-timeline__search-input"
                        placeholder="Buscar…"
                        value={timelineSearch}
                        onChange={(e) => setTimelineSearch(e.target.value)}
                        aria-label="Buscar en historial de farmacia y sanatorio"
                        title="Buscar medicamento, fecha, profesional u origen"
                      />
                      {timelineSearch.trim() ? (
                        <button
                          type="button"
                          className="crm-ac-timeline__search-clear"
                          onClick={() => setTimelineSearch('')}
                          aria-label="Limpiar búsqueda del historial"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                    <div className="crm-ac-timeline__meta">
                      <span>Mostrar</span>
                      <select
                        value={timelinePageSize <= 0 ? 'ALL' : String(timelinePageSize)}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'ALL') {
                            setTimelinePageSize(0);
                            setTimelinePage(1);
                          } else {
                            const n = parseInt(v, 10);
                            setTimelinePageSize(Number.isNaN(n) ? TIMELINE_DEFAULT_PAGE_SIZE : n);
                            setTimelinePage(1);
                          }
                        }}
                        aria-label="Cantidad de movimientos por página"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="ALL">Todos</option>
                      </select>
                      <span>por página</span>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="crm-ac-timeline__body">
                {historialEndpoint && historialLoading ? (
                  <div className="crm-ac-timeline-state" role="status" aria-live="polite" aria-busy="true">
                    <span className="crm-gd-spinner" aria-hidden="true" />
                    <p className="crm-ac-timeline-state__title">Cargando historial</p>
                    <p className="crm-ac-timeline-state__hint">Farmacia y sanatorio…</p>
                  </div>
                ) : timelineEvents.length === 0 ? (
                  <div className="crm-ac-timeline-state crm-ac-timeline-state--empty" role="status">
                    <span className="crm-ac-timeline-state__icon" aria-hidden="true">
                      📋
                    </span>
                    <p className="crm-ac-timeline-state__title">Sin movimientos</p>
                    <p className="crm-ac-timeline-state__hint">
                      No hay registros de farmacia ni sanatorio para este paciente.
                    </p>
                  </div>
                ) : timelineSearched.length === 0 ? (
                  <div
                    className="crm-ac-timeline-state crm-ac-timeline-state--no-match"
                    role="status"
                  >
                    <span className="crm-ac-timeline-state__icon" aria-hidden="true">
                      🔎
                    </span>
                    <p className="crm-ac-timeline-state__title">Sin coincidencias</p>
                    <p className="crm-ac-timeline-state__term" title={timelineSearch.trim()}>
                      {timelineSearch.trim().length > 32
                        ? `${timelineSearch.trim().slice(0, 32)}…`
                        : timelineSearch.trim()}
                    </p>
                    <p className="crm-ac-timeline-state__hint">
                      Probá con otro medicamento, fecha, origen o nombre del profesional.
                    </p>
                    <button
                      type="button"
                      className="crm-ac-timeline-state__action"
                      onClick={() => setTimelineSearch('')}
                    >
                      Limpiar búsqueda
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="crm-tl crm-ac-timeline__list">
                      {timelinePageItems.map((ev, i) => {
                        const isFarmacia = ev.origin === 'farmacia';
                        const originClass = isFarmacia ? 'farmacia' : 'sanatorio';
                        const prestadorLabel =
                          ev.origin === 'sanatorio' && ev.prestador
                            ? ev.prestador
                                .toLowerCase()
                                .split(' ')
                                .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                                .join(' ')
                                .replace(/^(\w+)\s(.+)$/, '$1, $2')
                            : null;

                        return (
                          <div
                            key={`${timelinePage}-${ev.date}-${ev.desc}-${i}`}
                            className={`crm-tl-item crm-ac-tl-item crm-ac-tl-item--${originClass}`}
                          >
                            <div className={`crm-tl-dot crm-ac-tl-dot crm-ac-tl-dot--${originClass}`}>
                              <span aria-hidden="true">{ev.ico}</span>
                            </div>
                            <div className={`crm-tl-card crm-ac-tl-card crm-ac-tl-card--${originClass}`}>
                              <div className="crm-tl-head crm-ac-tl-head">
                                <div className="crm-ac-tl-head__main">
                                  <span className={isFarmacia ? 'crm-ob-farm' : 'crm-ob-san'}>
                                    {isFarmacia ? 'Farmacia' : 'Sanatorio'}
                                  </span>
                                  {ev.origin === 'sanatorio' && c.servicio_nombre_geclisa ? (
                                    <span className="crm-tl-svc crm-ac-tl-svc">{c.servicio_nombre_geclisa}</span>
                                  ) : null}
                                </div>
                                <time className="crm-tl-date" dateTime={ev.date || undefined}>
                                  {formatDateTime(ev.date)}
                                </time>
                              </div>

                              {prestadorLabel ? (
                                <p className="crm-ac-tl-prestador">
                                  <span className="crm-ac-tl-prestador__label">Profesional</span>
                                  {prestadorLabel}
                                </p>
                              ) : null}

                              <p className="crm-ac-tl-desc">{ev.desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {timelinePageSize > 0 && timelineTotalPages > 1 ? (
                      <nav className="crm-ac-timeline__pager" aria-label="Paginación del historial">
                        <button
                          type="button"
                          className="crm-ac-timeline__pager-btn"
                          onClick={() => setTimelinePage((p) => Math.max(1, p - 1))}
                          disabled={timelinePage === 1}
                          aria-label="Página anterior"
                        >
                          ‹
                        </button>
                        <span className="crm-ac-timeline__pager-meta">
                          Página <strong>{timelinePage}</strong> de {timelineTotalPages}
                        </span>
                        <button
                          type="button"
                          className="crm-ac-timeline__pager-btn"
                          onClick={() => setTimelinePage((p) => Math.min(timelineTotalPages, p + 1))}
                          disabled={timelinePage === timelineTotalPages}
                          aria-label="Página siguiente"
                        >
                          ›
                        </button>
                      </nav>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Historial */}
        <div className={`crm-tab-panel ${profileTab === 'historial' ? 'active' : ''}`}>
          <div className="crm-hist-panel crm-ac-hist-tab">
            <div className="crm-ac-hist-tab__toolbar">
              <div className="crm-ac-hist-tab__filters" role="group" aria-label="Filtrar historial por origen">
                <button
                  type="button"
                  className={`crm-ac-hist-filter${histFilter === 'all' ? ' is-active' : ''}`}
                  aria-pressed={histFilter === 'all'}
                  onClick={() => setHistFilter('all')}
                >
                  Todos
                </button>
                <button
                  type="button"
                  className={`crm-ac-hist-filter crm-ac-hist-filter--farm${histFilter === 'farmacia' ? ' is-active' : ''}`}
                  aria-pressed={histFilter === 'farmacia'}
                  onClick={() => setHistFilter('farmacia')}
                >
                  <span aria-hidden="true">💊</span> Farmacia
                </button>
                <button
                  type="button"
                  className={`crm-ac-hist-filter crm-ac-hist-filter--san${histFilter === 'sanatorio' ? ' is-active' : ''}`}
                  aria-pressed={histFilter === 'sanatorio'}
                  onClick={() => setHistFilter('sanatorio')}
                >
                  <span aria-hidden="true">🏥</span> Sanatorio
                </button>
              </div>
              {timelineEvents.length > 0 ? (
                <>
                  <div className="crm-ac-hist-tab__search">
                    <span className="crm-ac-hist-tab__search-icon" aria-hidden="true">
                      🔎
                    </span>
                    <input
                      type="search"
                      className="crm-ac-hist-tab__search-input"
                      placeholder="Buscar…"
                      value={histSearch}
                      onChange={(e) => setHistSearch(e.target.value)}
                      aria-label="Buscar en historial"
                      title="Buscar por medicamento, fecha, profesional u origen"
                    />
                    {histSearch.trim() ? (
                      <button
                        type="button"
                        className="crm-ac-hist-tab__search-clear"
                        onClick={() => setHistSearch('')}
                        aria-label="Limpiar búsqueda del historial"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  <div className="crm-ac-hist-tab__meta">
                    <span
                      className={`crm-ac-hist-tab__count${
                        histSearch.trim() || histFilter !== 'all' ? ' crm-ac-hist-tab__count--filtered' : ''
                      }`}
                    >
                      {histSearch.trim() || histFilter !== 'all' ? (
                        <>
                          {historialSearched.length} de {timelineEvents.length}
                        </>
                      ) : (
                        <>
                          {timelineEvents.length} registro{timelineEvents.length === 1 ? '' : 's'}
                        </>
                      )}
                    </span>
                    <span className="crm-ac-hist-tab__meta-label">Mostrar</span>
                    <select
                      value={histPageSize <= 0 ? 'ALL' : String(histPageSize)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'ALL') {
                          setHistPageSize(0);
                          setHistPage(1);
                        } else {
                          const n = parseInt(v, 10);
                          setHistPageSize(Number.isNaN(n) ? 10 : n);
                          setHistPage(1);
                        }
                      }}
                      aria-label="Cantidad de registros por página"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="ALL">Todos</option>
                    </select>
                    <span className="crm-ac-hist-tab__meta-label">por página</span>
                  </div>
                </>
              ) : null}
            </div>

            <div className="crm-hist-content crm-ac-hist-tab__body">
              {historialEndpoint && historialLoading ? (
                <div className="crm-ac-hist-tab__state" role="status" aria-live="polite" aria-busy="true">
                  <span className="crm-gd-spinner" aria-hidden="true" />
                  <p className="crm-ac-hist-tab__state-title">Cargando historial</p>
                  <p className="crm-ac-hist-tab__state-hint">Farmacia y sanatorio…</p>
                </div>
              ) : timelineEvents.length === 0 ? (
                <div className="crm-ac-hist-tab__state crm-ac-hist-tab__state--empty" role="status">
                  <span className="crm-ac-hist-tab__state-icon" aria-hidden="true">
                    📋
                  </span>
                  <p className="crm-ac-hist-tab__state-title">Sin movimientos</p>
                  <p className="crm-ac-hist-tab__state-hint">
                    No hay registros de farmacia ni sanatorio para este paciente.
                  </p>
                </div>
              ) : historialSearched.length === 0 ? (
                <div className="crm-ac-hist-tab__state crm-ac-hist-tab__state--no-match" role="status">
                  <span className="crm-ac-hist-tab__state-icon" aria-hidden="true">
                    🔎
                  </span>
                  <p className="crm-ac-hist-tab__state-title">Sin coincidencias</p>
                  {histSearch.trim() ? (
                    <p className="crm-ac-hist-tab__state-term" title={histSearch.trim()}>
                      {histSearch.trim().length > 40
                        ? `${histSearch.trim().slice(0, 40)}…`
                        : histSearch.trim()}
                    </p>
                  ) : null}
                  <p className="crm-ac-hist-tab__state-hint">
                    {histFilter === 'farmacia'
                      ? 'No hay movimientos de farmacia con este criterio.'
                      : histFilter === 'sanatorio'
                        ? 'No hay movimientos de sanatorio con este criterio.'
                        : 'Probá otro término de búsqueda o cambiá el filtro de origen.'}
                  </p>
                  <div className="crm-ac-hist-tab__state-actions">
                    {histSearch.trim() ? (
                      <button
                        type="button"
                        className="crm-ac-hist-tab__state-btn"
                        onClick={() => setHistSearch('')}
                      >
                        Limpiar búsqueda
                      </button>
                    ) : null}
                    {histFilter !== 'all' ? (
                      <button
                        type="button"
                        className="crm-ac-hist-tab__state-btn crm-ac-hist-tab__state-btn--ghost"
                        onClick={() => setHistFilter('all')}
                      >
                        Ver todos
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  <div className="crm-ac-hist-tab__scroll">
                  <div className="crm-ac-hist-tab__table-wrap">
                    <table className="crm-profile-table crm-ac-hist-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Origen</th>
                          <th>Producto / Servicio</th>
                          <th>Detalle</th>
                          <th>Profesional</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historialPageItems.map((ev, i) => {
                          const isFarmacia = ev.origin === 'farmacia';
                          const productLabel =
                            ev.desc?.trim() ||
                            (isFarmacia
                              ? 'Movimiento en farmacia'
                              : c.practica_nombre_geclisa?.trim() || ev.svc || '—');
                          const detailLabel = isFarmacia
                            ? ev.note?.trim() || '—'
                            : c.practica_nombre_geclisa?.trim() || ev.note?.trim() || '—';
                          const svcSub =
                            !isFarmacia && ev.svc && ev.svc !== 'Sanatorio' ? ev.svc : null;
                          const prestadorLabel = formatPrestadorLabel(ev.prestador);

                          return (
                            <tr
                              key={`hist-${histPage}-${ev.date}-${i}`}
                              className={`crm-ac-hist-row crm-ac-hist-row--${isFarmacia ? 'farmacia' : 'sanatorio'}`}
                            >
                              <td className="crm-ac-hist-cell-date">
                                <time dateTime={ev.date || undefined}>{formatDateTime(ev.date)}</time>
                              </td>
                              <td>
                                <span className={isFarmacia ? 'crm-ob-farm' : 'crm-ob-san'}>
                                  {isFarmacia ? 'Farmacia' : 'Sanatorio'}
                                </span>
                              </td>
                              <td className="crm-ac-hist-cell-product">
                                <span className="crm-ac-hist-product">{productLabel}</span>
                                {svcSub ? <span className="crm-ac-hist-sub">{svcSub}</span> : null}
                              </td>
                              <td className="crm-ac-hist-cell-detail">
                                {detailLabel !== productLabel ? detailLabel : '—'}
                              </td>
                              <td className="crm-ac-hist-cell-pro">
                                {prestadorLabel || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="crm-rec-cards crm-ac-hist-tab__cards">
                    {historialPageItems.map((ev, i) => {
                      const isFarmacia = ev.origin === 'farmacia';
                      const productLabel =
                        ev.desc?.trim() ||
                        (isFarmacia
                          ? 'Movimiento en farmacia'
                          : c.practica_nombre_geclisa?.trim() || ev.svc || '—');
                      const detailLabel = isFarmacia
                        ? ev.note?.trim() || '—'
                        : c.practica_nombre_geclisa?.trim() || ev.note?.trim() || '—';
                      const prestadorLabel = formatPrestadorLabel(ev.prestador);

                      return (
                        <article
                          key={`hist-card-${histPage}-${ev.date}-${i}`}
                          className={`crm-ac-hist-card crm-ac-hist-card--${isFarmacia ? 'farmacia' : 'sanatorio'}`}
                        >
                          <div className="crm-ac-hist-card__head">
                            <span className={isFarmacia ? 'crm-ob-farm' : 'crm-ob-san'}>
                              {isFarmacia ? 'Farmacia' : 'Sanatorio'}
                            </span>
                            <time className="crm-ac-hist-card__date" dateTime={ev.date || undefined}>
                              {formatDateTime(ev.date)}
                            </time>
                          </div>
                          <p className="crm-ac-hist-card__product">{productLabel}</p>
                          {detailLabel !== productLabel && detailLabel !== '—' ? (
                            <p className="crm-ac-hist-card__detail">{detailLabel}</p>
                          ) : null}
                          {prestadorLabel ? (
                            <p className="crm-ac-hist-card__pro">
                              <span className="crm-ac-hist-card__pro-label">Profesional</span>
                              {prestadorLabel}
                            </p>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                  </div>

                  {histPageSize > 0 && histTotalPages > 1 ? (
                    <nav className="crm-ac-hist-tab__pager" aria-label="Paginación del historial">
                      <button
                        type="button"
                        className="crm-ac-hist-tab__pager-btn"
                        onClick={() => setHistPage((p) => Math.max(1, p - 1))}
                        disabled={histPage === 1}
                        aria-label="Página anterior"
                      >
                        ‹
                      </button>
                      <span className="crm-ac-hist-tab__pager-meta">
                        Página <strong>{histPage}</strong> de {histTotalPages}
                      </span>
                      <button
                        type="button"
                        className="crm-ac-hist-tab__pager-btn"
                        onClick={() => setHistPage((p) => Math.min(histTotalPages, p + 1))}
                        disabled={histPage === histTotalPages}
                        aria-label="Página siguiente"
                      >
                        ›
                      </button>
                    </nav>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Recetas — recetas (API por DNI) + facturación PLEX; se pide al seleccionar cliente para cargar rápido */}
        <div className={`crm-tab-panel ${profileTab === 'recetas' ? 'active' : ''}`}>
          <div className="crm-recetas-panel crm-ac-recetas-tab">
            {recetasLoading && !recetas.length ? (
              <div className="crm-ac-recetas-tab__state" role="status" aria-live="polite" aria-busy="true">
                <span className="crm-gd-spinner" aria-hidden="true" />
                <p className="crm-ac-recetas-tab__state-title">Cargando recetas</p>
                <p className="crm-ac-recetas-tab__state-hint">GECLISA y PLEX…</p>
              </div>
            ) : recetasError && !recetas.length ? (
              <div className="crm-ac-recetas-tab__state crm-ac-recetas-tab__state--error" role="alert">
                <span className="crm-ac-recetas-tab__state-icon" aria-hidden="true">
                  ⚠
                </span>
                <p className="crm-ac-recetas-tab__state-title">No se pudieron cargar las recetas</p>
                <p className="crm-ac-recetas-tab__state-hint">{recetasError}</p>
              </div>
            ) : (
              <>
                <section
                  className={`crm-ac-rec-section crm-ac-rec-section--san crm-ac-accordion crm-ac-rec-accordion${
                    recetasAccordion === 'san' ? ' is-open' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="crm-ac-accordion__trigger crm-ac-rec-accordion__trigger crm-ac-rec-accordion__trigger--san"
                    aria-expanded={recetasAccordion === 'san'}
                    onClick={() => toggleRecetasAccordion('san')}
                  >
                    <span className="crm-ac-rec-accordion__trigger-main">
                      <span className="crm-ac-rec-section__icon" aria-hidden="true">
                        🏥
                      </span>
                      <span className="crm-ac-accordion__title">{sanatorioRecetasTitle}</span>
                      <RecetasCountBadge
                        filteredCount={filteredRecetasGeclisa.length}
                        totalCount={recetasGeclisa.length}
                        itemCount={itemsGeclisa.length}
                        searchActive={!!recGeSearch.trim()}
                      />
                      {recetasLoading ? (
                        <span className="crm-ac-rec-section__spinner" aria-hidden="true" />
                      ) : null}
                    </span>
                    <span className="crm-ac-accordion__chev" aria-hidden="true" />
                  </button>
                  <div className="crm-ac-accordion__panel">
                    <div className="crm-ac-rec-accordion__inner">
                  <AcRecetasToolbar
                    variant="san"
                    icon="🏥"
                    title={sanatorioRecetasTitle}
                    searchPlaceholder="Buscar…"
                    searchValue={recGeSearch}
                    onSearchChange={setRecGeSearch}
                    pageSize={recGePageSize}
                    onPageSizeChange={(n) => {
                      setRecGePageSize(n);
                      setRecGePage(1);
                    }}
                    filteredCount={filteredRecetasGeclisa.length}
                    totalCount={recetasGeclisa.length}
                    itemCount={itemsGeclisa.length}
                    searchActive={!!recGeSearch.trim()}
                    loading={recetasLoading}
                    toolbarOnly
                  />
                  <div className="crm-ac-rec-section__body">
                  {recetasLoading && recetasGeclisa.length === 0 ? (
                    <div className="crm-ac-rec-section__state" role="status" aria-busy="true">
                      <span className="crm-gd-spinner" aria-hidden="true" />
                      <p className="crm-ac-rec-section__state-title">Cargando recetas GECLISA</p>
                    </div>
                  ) : recetasGeclisa.length === 0 ? (
                    <AcRecetasEmpty variant="san" mode="empty" />
                  ) : filteredRecetasGeclisa.length === 0 ? (
                    <AcRecetasEmpty
                      variant="san"
                      mode="no-match"
                      searchTerm={recGeSearch}
                      onClearSearch={() => setRecGeSearch('')}
                    />
                  ) : (
                    <>
                      <div className="crm-ac-rec-section__scroll">
                      <div className="crm-ac-rec-section__table-wrap">
                      <table className="crm-profile-table crm-ac-rec-table">
                        <thead>
                          <tr>
                            <th style={{ width: '18%', minWidth: 140 }}>Receta</th>
                            <th>Genérico</th>
                            <th>Presentación</th>
                            <th>Cant.</th>
                            <th>Frecuencia</th>
                            <th>Sugerencia</th>
                            <th>Observaciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recGePageItems.map((r, i) => {
                            const idReceta = r.rec_enca_id_geclisa;
                            const itemsDeReceta = (idReceta != null ? itemsGeclisaByReceta.get(idReceta) : null) || [];
                            const recetaLabel = [
                              formatDate(r.rec_fecha_geclisa) && `${formatDate(r.rec_fecha_geclisa)}`,
                              idReceta != null && `#${idReceta}`,
                              (r.diagnostico_geclisa || '').trim() && (r.diagnostico_geclisa || '').trim().slice(0, 30) + ((r.diagnostico_geclisa || '').length > 30 ? '…' : '')
                            ].filter(Boolean).join(' · ') || '—';
                            const recLink = r.rec_link_geclisa;

                            if (itemsDeReceta.length === 0) {
                              return (
                                <tr key={r.rec_enca_id_geclisa != null ? `gec-${r.rec_enca_id_geclisa}-${i}` : `gec-${i}`}>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <span>{recetaLabel}</span>
                                      {recLink && (
                                        <a
                                          href={recLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            alignSelf: 'flex-start',
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            border: '1px solid #2365AB',
                                            backgroundColor: 'rgba(35,101,171,0.08)',
                                            color: '#2365AB',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <span style={{ fontSize: 13 }}>🧾</span>
                                          <span>Ver receta digital</span>
                                        </a>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openRecetaPrintWindow({
                                            title: 'Receta GECLISA',
                                            paciente: pacienteForPrint,
                                            recetaInfo: {
                                              origen: 'GECLISA',
                                              idVisible: idReceta,
                                              texto: [
                                                formatDate(r.rec_fecha_geclisa) && `${formatDate(r.rec_fecha_geclisa)}`,
                                                idReceta != null && `#${idReceta}`,
                                                (r.diagnostico_geclisa || '').trim()
                                              ].filter(Boolean).join(' · '),
                                              obraSocial: c.os_sigla_geclisa?.trim() || null,
                                              prestador: c.prestador_geclisa?.trim() || null
                                            },
                                            items: [],
                                            columns: [
                                              { header: 'Genérico', accessor: (it) => it.generico ?? '—' },
                                              { header: 'Presentación', accessor: (it) => it.presentacion ?? '—' },
                                              { header: 'Cant.', accessor: (it) => (it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—') },
                                              { header: 'Frecuencia', accessor: (it) => it.frecuencia ?? '—' },
                                              { header: 'Sugerencia', accessor: (it) => it.sugerencia ?? '—' },
                                              { header: 'Observaciones', accessor: (it) => it.item_observaciones ?? '—' }
                                            ]
                                          })
                                        }
                                        style={{
                                          marginTop: 4,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          alignSelf: 'flex-start',
                                          padding: '4px 10px',
                                          borderRadius: 999,
                                          border: '1px solid #CBD6E2',
                                          backgroundColor: '#ffffff',
                                          color: '#2563eb',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
                                        }}
                                      >
                                        <span style={{ fontSize: 13 }}>📄</span>
                                        <span>Ver PDF receta</span>
                                      </button>
                                    </div>
                                  </td>
                                  <td colSpan={6} style={{ color: '#7C98B6', fontSize: 13 }}>Sin ítems</td>
                                </tr>
                              );
                            }
                            return itemsDeReceta.map((it, j) => (
                              <tr key={`gec-${idReceta}-${i}-${it.rec_item_id ?? j}-${j}`}>
                                {j === 0 && (
                                  <td
                                    rowSpan={itemsDeReceta.length}
                                  >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <span>{recetaLabel}</span>
                                      {recLink && (
                                        <a
                                          href={recLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            alignSelf: 'flex-start',
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            border: '1px solid #2365AB',
                                            backgroundColor: 'rgba(35,101,171,0.08)',
                                            color: '#2365AB',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <span style={{ fontSize: 13 }}>🧾</span>
                                          <span>Ver receta digital</span>
                                        </a>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openRecetaPrintWindow({
                                            title: 'Receta GECLISA',
                                            paciente: pacienteForPrint,
                                            recetaInfo: {
                                              origen: 'GECLISA',
                                              idVisible: idReceta,
                                              texto: [
                                                formatDate(r.rec_fecha_geclisa) && `${formatDate(r.rec_fecha_geclisa)}`,
                                                idReceta != null && `#${idReceta}`,
                                                (r.diagnostico_geclisa || '').trim()
                                              ].filter(Boolean).join(' · '),
                                              obraSocial: c.os_sigla_geclisa?.trim() || null,
                                              prestador: c.prestador_geclisa?.trim() || null
                                            },
                                            items: itemsDeReceta,
                                            columns: [
                                              { header: 'Genérico', accessor: (x) => x.generico ?? '—' },
                                              { header: 'Presentación', accessor: (x) => x.presentacion ?? '—' },
                                              { header: 'Cant.', accessor: (x) => (x.cantidad != null ? parseInt(Number(x.cantidad), 10) : '—') },
                                              { header: 'Frecuencia', accessor: (x) => x.frecuencia ?? '—' },
                                              { header: 'Sugerencia', accessor: (x) => x.sugerencia ?? '—' },
                                              { header: 'Observaciones', accessor: (x) => x.item_observaciones ?? '—' }
                                            ]
                                          })
                                        }
                                        style={{
                                          marginTop: 4,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          alignSelf: 'flex-start',
                                          padding: '4px 10px',
                                          borderRadius: 999,
                                          border: '1px solid #CBD6E2',
                                          backgroundColor: '#ffffff',
                                          color: '#2563eb',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
                                        }}
                                      >
                                        <span style={{ fontSize: 13 }}>📄</span>
                                        <span>Ver PDF receta</span>
                                      </button>
                                    </div>
                                  </td>
                                )}
                                <td>{it.generico ?? '—'}</td>
                                <td>{it.presentacion ?? '—'}</td>
                                <td>{it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—'}</td>
                                <td>{it.frecuencia ?? '—'}</td>
                                <td>{it.sugerencia ?? '—'}</td>
                                <td>{it.item_observaciones ?? '—'}</td>
                              </tr>
                            ));
                          })}
                        </tbody>
                      </table>
                      </div>
                      <div className="crm-rec-cards crm-ac-rec-section__cards">
                        {recGePageItems.map((r, i) => {
                          const idReceta = r.rec_enca_id_geclisa;
                          const itemsDeReceta = (idReceta != null ? itemsGeclisaByReceta.get(idReceta) : null) || [];
                          const recetaLabel = [
                            formatDate(r.rec_fecha_geclisa) && `${formatDate(r.rec_fecha_geclisa)}`,
                            idReceta != null && `#${idReceta}`,
                            (r.diagnostico_geclisa || '').trim() && (r.diagnostico_geclisa || '').trim().slice(0, 30) + ((r.diagnostico_geclisa || '').length > 30 ? '…' : '')
                          ].filter(Boolean).join(' · ') || '—';
                          const recLink = r.rec_link_geclisa;

                          return (
                            <div
                              key={r.rec_enca_id_geclisa != null ? `gec-card-${r.rec_enca_id_geclisa}-${i}` : `gec-card-${i}`}
                              className="crm-rec-card"
                            >
                              <div className="crm-rec-card-top">
                                <div className="crm-rec-card-label">{recetaLabel}</div>
                                <div className="crm-rec-card-actions">
                                  {recLink && (
                                    <a
                                      href={recLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="crm-ac-rec-btn crm-ac-rec-btn--digital"
                                    >
                                      <span style={{ fontSize: 13 }}>🧾</span>
                                      <span>Ver receta digital</span>
                                    </a>
                                  )}
                                  <button
                                    type="button"
                                    className="crm-ac-rec-btn crm-ac-rec-btn--pdf"
                                    onClick={() =>
                                      openRecetaPrintWindow({
                                        title: 'Receta GECLISA',
                                        paciente: pacienteForPrint,
                                        recetaInfo: {
                                          origen: 'GECLISA',
                                          idVisible: idReceta,
                                          texto: [
                                            formatDate(r.rec_fecha_geclisa) && `${formatDate(r.rec_fecha_geclisa)}`,
                                            idReceta != null && `#${idReceta}`,
                                            (r.diagnostico_geclisa || '').trim()
                                          ].filter(Boolean).join(' · '),
                                          obraSocial: c.os_sigla_geclisa?.trim() || null,
                                          prestador: c.prestador_geclisa?.trim() || null
                                        },
                                        items: itemsDeReceta,
                                        columns: [
                                          { header: 'Genérico', accessor: (it) => it.generico ?? '—' },
                                          { header: 'Presentación', accessor: (it) => it.presentacion ?? '—' },
                                          { header: 'Cant.', accessor: (it) => (it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—') },
                                          { header: 'Frecuencia', accessor: (it) => it.frecuencia ?? '—' },
                                          { header: 'Sugerencia', accessor: (it) => it.sugerencia ?? '—' },
                                          { header: 'Observaciones', accessor: (it) => it.item_observaciones ?? '—' }
                                        ]
                                      })
                                    }
                                  >
                                    <span style={{ fontSize: 13 }}>📄</span>
                                    <span>Ver PDF receta</span>
                                  </button>
                                </div>
                              </div>

                              {itemsDeReceta.length === 0 ? (
                                <div className="crm-rec-card-empty">Sin ítems</div>
                              ) : (
                                <div className="crm-rec-card-list">
                                  {itemsDeReceta.map((it, j) => (
                                    <div key={`gec-card-item-${idReceta}-${it.rec_item_id ?? j}-${j}`} className="crm-rec-card-item">
                                      <div className="crm-rec-card-grid">
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Genérico</span>
                                          <span className="crm-rec-card-field-value">{it.generico ?? '—'}</span>
                                        </div>
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Presentación</span>
                                          <span className="crm-rec-card-field-value">{it.presentacion ?? '—'}</span>
                                        </div>
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Cant.</span>
                                          <span className="crm-rec-card-field-value">{it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—'}</span>
                                        </div>
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Frecuencia</span>
                                          <span className="crm-rec-card-field-value">{it.frecuencia ?? '—'}</span>
                                        </div>
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Sugerencia</span>
                                          <span className="crm-rec-card-field-value">{it.sugerencia ?? '—'}</span>
                                        </div>
                                        <div className="crm-rec-card-field">
                                          <span className="crm-rec-card-field-label">Observaciones</span>
                                          <span className="crm-rec-card-field-value">{it.item_observaciones ?? '—'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      </div>
                      {recGePageSize > 0 ? (
                        <AcRecetasPager
                          page={recGePage}
                          totalPages={recGeTotalPages}
                          onPrev={() => setRecGePage((p) => Math.max(1, p - 1))}
                          onNext={() => setRecGePage((p) => Math.min(recGeTotalPages, p + 1))}
                        />
                      ) : null}
                    </>
                  )}
                  </div>
                    </div>
                  </div>
                </section>
                <section
                  className={`crm-ac-rec-section crm-ac-rec-section--farm crm-ac-accordion crm-ac-rec-accordion${
                    recetasAccordion === 'farm' ? ' is-open' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="crm-ac-accordion__trigger crm-ac-rec-accordion__trigger crm-ac-rec-accordion__trigger--farm"
                    aria-expanded={recetasAccordion === 'farm'}
                    onClick={() => toggleRecetasAccordion('farm')}
                  >
                    <span className="crm-ac-rec-accordion__trigger-main">
                      <span className="crm-ac-rec-section__icon" aria-hidden="true">
                        💊
                      </span>
                      <span className="crm-ac-accordion__title">
                        Recetas dispensadas — Farmacia Santa Ana
                      </span>
                      <RecetasCountBadge
                        filteredCount={filteredRecetasPlex.length}
                        totalCount={recetasPlex.length}
                        itemCount={itemsPlex.length}
                        searchActive={!!recPlexSearch.trim()}
                      />
                      {recetasLoading ? (
                        <span className="crm-ac-rec-section__spinner" aria-hidden="true" />
                      ) : null}
                    </span>
                    <span className="crm-ac-accordion__chev" aria-hidden="true" />
                  </button>
                  <div className="crm-ac-accordion__panel">
                    <div className="crm-ac-rec-accordion__inner">
                  <AcRecetasToolbar
                    variant="farm"
                    icon="💊"
                    title="Recetas dispensadas — Farmacia Santa Ana"
                    searchPlaceholder="Buscar…"
                    searchValue={recPlexSearch}
                    onSearchChange={setRecPlexSearch}
                    pageSize={recPlexPageSize}
                    onPageSizeChange={(n) => {
                      setRecPlexPageSize(n);
                      setRecPlexPage(1);
                    }}
                    filteredCount={filteredRecetasPlex.length}
                    totalCount={recetasPlex.length}
                    itemCount={itemsPlex.length}
                    searchActive={!!recPlexSearch.trim()}
                    loading={recetasLoading}
                    toolbarOnly
                  />
                  <div className="crm-ac-rec-section__body">
                  {recetasLoading && recetasPlex.length === 0 ? (
                    <div className="crm-ac-rec-section__state" role="status" aria-busy="true">
                      <span className="crm-gd-spinner" aria-hidden="true" />
                      <p className="crm-ac-rec-section__state-title">Cargando recetas PLEX</p>
                    </div>
                  ) : recetasPlex.length === 0 ? (
                    <AcRecetasEmpty variant="farm" mode="empty" />
                  ) : filteredRecetasPlex.length === 0 ? (
                    <AcRecetasEmpty
                      variant="farm"
                      mode="no-match"
                      searchTerm={recPlexSearch}
                      onClearSearch={() => setRecPlexSearch('')}
                    />
                  ) : (
                    <>
                      <div className="crm-ac-rec-section__scroll">
                      <div className="crm-ac-rec-section__table-wrap">
                      <table className="crm-profile-table crm-ac-rec-table crm-ac-rec-table--plex">
                        <thead>
                          <tr>
                            <th style={{ width: '18%', minWidth: 140 }}>Receta - #ID</th>
                            <th>Renglón</th>
                            <th>ID producto</th>
                            <th>Producto</th>
                            <th>Presentación</th>
                            <th>Cant.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recPlexPageItems.map((r, i) => {
                            const idReceta = r.idreceta_plex;
                            const itemsDeReceta = (idReceta != null ? itemsPlexByReceta.get(idReceta) : null) || [];
                            const rowCount = Math.max(1, itemsDeReceta.length);
                            const recetaLabel = [
                              formatDate(r.fechaemision_plex) && `${formatDate(r.fechaemision_plex)}`,
                              (r.numreceta_plex ?? r.idreceta_plex) != null && `#${(r.numreceta_plex ?? r.idreceta_plex)}`,
                              formatDate(r.fechavencimiento_plex) && `Vto ${formatDate(r.fechavencimiento_plex)}`
                            ].filter(Boolean).join(' · ') || '—';
                            if (itemsDeReceta.length === 0) {
                              return (
                                <tr key={r.idreceta_plex != null ? `plex-${r.idreceta_plex}-${i}` : `plex-${i}`}>
                                  <td className="crm-ac-rec-receta-cell crm-ac-rec-receta-cell--farm">
                                    <div className="crm-ac-rec-receta-cell__inner">
                                      <span className="crm-ac-rec-receta-label">{recetaLabel}</span>
                                      <button
                                        type="button"
                                        className="crm-ac-rec-btn crm-ac-rec-btn--pdf"
                                        onClick={() =>
                                          openRecetaPrintWindow({
                                            title: 'Receta PLEX',
                                            paciente: pacienteForPrint,
                                            recetaInfo: {
                                              origen: 'PLEX',
                                              idVisible: (r.numreceta_plex ?? r.idreceta_plex) ?? null,
                                              texto: recetaLabel,
                                              obraSocial: c.obra_social_descripcio?.trim() || null
                                            },
                                            items: [],
                                            columns: [
                                              { header: 'Renglón', accessor: (x) => x.renglon ?? '—' },
                                              { header: 'ID producto', accessor: (x) => x.idproducto ?? '—' },
                                              { header: 'Producto', accessor: (x) => x.producto ?? '—' },
                                              { header: 'Presentación', accessor: (x) => x.presentaci ?? '—' },
                                              { header: 'Cant.', accessor: (x) => (x.cantidad != null ? parseInt(Number(x.cantidad), 10) : '—') }
                                            ]
                                          })
                                        }
                                      >
                                        <span style={{ fontSize: 13 }}>📄</span>
                                        <span>Ver PDF receta</span>
                                      </button>
                                    </div>
                                  </td>
                                  <td colSpan={5} style={{ color: '#7C98B6', fontSize: 13 }}>Sin ítems</td>
                                </tr>
                              );
                            }
                            return itemsDeReceta.map((it, j) => {
                              const anuladaItem = isPlexItemAnulada(it);

                              return (
                                <tr key={`plex-${idReceta}-${i}-${it.renglon ?? j}-${j}`}>
                                  {j === 0 && (
                                    <td
                                      rowSpan={itemsDeReceta.length}
                                      className="crm-ac-rec-receta-cell crm-ac-rec-receta-cell--farm"
                                    >
                                      <div className="crm-ac-rec-receta-cell__inner">
                                        <span className="crm-ac-rec-receta-label">{recetaLabel}</span>
                                        <button
                                          type="button"
                                          className="crm-ac-rec-btn crm-ac-rec-btn--pdf"
                                          onClick={() =>
                                            openRecetaPrintWindow({
                                              title: 'Receta PLEX',
                                              paciente: pacienteForPrint,
                                              recetaInfo: {
                                                origen: 'PLEX',
                                                idVisible: (r.numreceta_plex ?? r.idreceta_plex) ?? null,
                                                texto: recetaLabel,
                                                obraSocial: c.obra_social_descripcio?.trim() || null
                                              },
                                              items: itemsDeReceta,
                                              columns: [
                                                { header: 'Renglón', accessor: (x) => x.renglon ?? '—' },
                                                { header: 'ID producto', accessor: (x) => x.idproducto ?? '—' },
                                                { header: 'Producto', accessor: (x) => x.producto ?? '—' },
                                                { header: 'Presentación', accessor: (x) => x.presentaci ?? '—' },
                                                { header: 'Cant.', accessor: (x) => (x.cantidad != null ? parseInt(Number(x.cantidad), 10) : '—') }
                                              ]
                                            })
                                          }
                                        >
                                          <span style={{ fontSize: 13 }}>📄</span>
                                          <span>Ver PDF receta</span>
                                        </button>
                                      </div>
                                    </td>
                                  )}
                                  <td>
                                    <span className="crm-ac-rec-line-meta">
                                      <span className="crm-ac-rec-renglon" title="Renglón">
                                        <span aria-hidden="true">💊</span>
                                        <span>{it.renglon ?? '—'}</span>
                                      </span>
                                      <PlexItemStatus anulada={anuladaItem} />
                                    </span>
                                  </td>
                                  <td>{it.idproducto ?? '—'}</td>
                                  <td>{it.producto ?? '—'}</td>
                                  <td>{it.presentaci ?? '—'}</td>
                                  <td>{it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—'}</td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                      </div>
                      <div className="crm-rec-cards crm-ac-rec-section__cards">
                        {recPlexPageItems.map((r, i) => {
                          const idReceta = r.idreceta_plex;
                          const itemsDeReceta = (idReceta != null ? itemsPlexByReceta.get(idReceta) : null) || [];
                          const recetaLabel = [
                            formatDate(r.fechaemision_plex) && `${formatDate(r.fechaemision_plex)}`,
                            (r.numreceta_plex ?? r.idreceta_plex) != null && `#${(r.numreceta_plex ?? r.idreceta_plex)}`,
                            formatDate(r.fechavencimiento_plex) && `Vto ${formatDate(r.fechavencimiento_plex)}`
                          ].filter(Boolean).join(' · ') || '—';

                          return (
                            <div
                              key={r.idreceta_plex != null ? `plex-card-${r.idreceta_plex}-${i}` : `plex-card-${i}`}
                              className="crm-rec-card"
                            >
                              <div className="crm-rec-card-top">
                                <div className="crm-rec-card-label">{recetaLabel}</div>
                                <div className="crm-rec-card-actions">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openRecetaPrintWindow({
                                        title: 'Receta PLEX',
                                        paciente: pacienteForPrint,
                                        recetaInfo: {
                                          origen: 'PLEX',
                                          idVisible: (r.numreceta_plex ?? r.idreceta_plex) ?? null,
                                          texto: recetaLabel,
                                          obraSocial: c.obra_social_descripcio?.trim() || null
                                        },
                                        items: itemsDeReceta,
                                        columns: [
                                          { header: 'Renglón', accessor: (x) => x.renglon ?? '—' },
                                          { header: 'ID producto', accessor: (x) => x.idproducto ?? '—' },
                                          { header: 'Producto', accessor: (x) => x.producto ?? '—' },
                                          { header: 'Presentación', accessor: (x) => x.presentaci ?? '—' },
                                          { header: 'Cant.', accessor: (x) => (x.cantidad != null ? parseInt(Number(x.cantidad), 10) : '—') }
                                        ]
                                      })
                                    }
                                    className="crm-ac-rec-btn crm-ac-rec-btn--pdf"
                                  >
                                    <span style={{ fontSize: 13 }}>📄</span>
                                    <span>Ver PDF receta</span>
                                  </button>
                                </div>
                              </div>

                              {itemsDeReceta.length === 0 ? (
                                <div className="crm-rec-card-empty">Sin ítems</div>
                              ) : (
                                <div className="crm-rec-card-list">
                                  {itemsDeReceta.map((it, j) => {
                                    const anuladaItem = isPlexItemAnulada(it);

                                    return (
                                      <div
                                        key={`plex-card-item-${idReceta}-${it.renglon ?? j}-${j}`}
                                        className="crm-rec-card-item"
                                      >
                                        <div className="crm-rec-card-grid">
                                          <div className="crm-rec-card-field">
                                            <span className="crm-rec-card-field-label">Renglón</span>
                                            <span className="crm-rec-card-field-value">
                                              <span className="crm-ac-rec-line-meta">
                                                <span className="crm-ac-rec-renglon" title="Renglón">
                                                  <span aria-hidden="true">💊</span>
                                                  <span>{it.renglon ?? '—'}</span>
                                                </span>
                                                <PlexItemStatus anulada={anuladaItem} />
                                              </span>
                                            </span>
                                          </div>
                                          <div className="crm-rec-card-field">
                                            <span className="crm-rec-card-field-label">ID producto</span>
                                            <span className="crm-rec-card-field-value">{it.idproducto ?? '—'}</span>
                                          </div>
                                          <div className="crm-rec-card-field">
                                            <span className="crm-rec-card-field-label">Producto</span>
                                            <span className="crm-rec-card-field-value">{it.producto ?? '—'}</span>
                                          </div>
                                          <div className="crm-rec-card-field">
                                            <span className="crm-rec-card-field-label">Presentación</span>
                                            <span className="crm-rec-card-field-value">{it.presentaci ?? '—'}</span>
                                          </div>
                                          <div className="crm-rec-card-field">
                                            <span className="crm-rec-card-field-label">Cant.</span>
                                            <span className="crm-rec-card-field-value">
                                              {it.cantidad != null ? parseInt(Number(it.cantidad), 10) : '—'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      </div>
                      {recPlexPageSize > 0 ? (
                        <AcRecetasPager
                          page={recPlexPage}
                          totalPages={recPlexTotalPages}
                          onPrev={() => setRecPlexPage((p) => Math.max(1, p - 1))}
                          onNext={() => setRecPlexPage((p) => Math.min(recPlexTotalPages, p + 1))}
                        />
                      ) : null}
                    </>
                  )}
                  </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>

        {/* Tab Vínculos */}
        <div className={`crm-tab-panel ${profileTab === 'vinculos' ? 'active' : ''}`}>
          <div className="crm-recetas-panel crm-ac-vinculos-tab">
            <div className="crm-ac-vinculos-grid">
              <section className="crm-ac-vinculos-card crm-ac-vinculos-card--form">
                <header className="crm-ac-vinculos-card__head">
                  <div className="crm-ac-vinculos-card__head-main">
                    <span className="crm-ac-vinculos-card__icon" aria-hidden="true">
                      👥
                    </span>
                    <h3 className="crm-ac-vinculos-card__title">Cargar vínculo familiar</h3>
                  </div>
                  <p className="crm-ac-vinculos-card__titular">
                    <span className="crm-ac-vinculos-card__titular-label">Titular</span>
                    <span className="crm-ac-vinculos-card__titular-value">
                      {displayName} · DNI {formatDni(selectedCliente?.dni_normalizado) || '—'}
                    </span>
                  </p>
                </header>

                <div className="crm-ac-vinculos-form">
                  <div className="crm-ac-vinculos-field crm-ac-vinculos-field--full">
                    <label className="crm-ac-vinculos-label" htmlFor="ac-vinculo-search">
                      Buscar familiar por DNI o nombre
                    </label>
                    <div className="crm-ac-vinculos-search">
                      <span className="crm-ac-vinculos-search__icon" aria-hidden="true">
                        🔎
                      </span>
                      <input
                        id="ac-vinculo-search"
                        type="search"
                        className="crm-ac-vinculos-search__input"
                        value={vinculoSearch}
                        onChange={(e) => setVinculoSearch(e.target.value)}
                        placeholder="DNI o nombre del familiar…"
                      />
                      {vinculoSearch.trim() ? (
                        <button
                          type="button"
                          className="crm-ac-vinculos-search__clear"
                          onClick={() => setVinculoSearch('')}
                          aria-label="Limpiar búsqueda de familiar"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="crm-ac-vinculos-field">
                    <label className="crm-ac-vinculos-label" htmlFor="ac-vinculo-familiar">
                      Familiar
                    </label>
                    <select
                      id="ac-vinculo-familiar"
                      className="crm-ac-vinculos-select"
                      value={selectedVinculoClienteId}
                      onChange={(e) => setSelectedVinculoClienteId(e.target.value)}
                      disabled={vinculoSearchLoading}
                    >
                      <option value="">
                        {vinculoSearchLoading
                          ? 'Buscando…'
                          : vinculoCandidates.length
                            ? 'Seleccionar familiar'
                            : 'Sin resultados'}
                      </option>
                      {vinculoCandidates.map((cand) => (
                        <option key={`fam-${cand.codcliente}`} value={String(cand.codcliente)}>
                          {getDisplayName(cand)} · DNI {formatDni(cand.dni_normalizado) || '—'}
                        </option>
                      ))}
                    </select>
                    {!vinculoSearchLoading &&
                    vinculoSearch.trim() &&
                    vinculoCandidates.length === 0 ? (
                      <p className="crm-ac-vinculos-field__hint">Sin coincidencias para esta búsqueda.</p>
                    ) : null}
                  </div>

                  <div className="crm-ac-vinculos-field">
                    <label className="crm-ac-vinculos-label" htmlFor="ac-vinculo-tipo">
                      Tipo de vínculo
                    </label>
                    <select
                      id="ac-vinculo-tipo"
                      className="crm-ac-vinculos-select"
                      value={selectedVinculoTipo}
                      onChange={(e) => setSelectedVinculoTipo(e.target.value)}
                    >
                      <option value="">Seleccionar vínculo</option>
                      {vinculoTipos.map((tipo) => (
                        <option key={`tipo-${tipo}`} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="crm-ac-vinculos-actions">
                  <button
                    type="button"
                    className="crm-ac-vinculos-btn crm-ac-vinculos-btn--primary"
                    onClick={guardarVinculo}
                    disabled={vinculoSaving || !selectedVinculoClienteId || !selectedVinculoTipo}
                    title={
                      vinculoSaving || !selectedVinculoClienteId || !selectedVinculoTipo
                        ? 'Completá familiar y tipo de vínculo'
                        : 'Guardar vínculo'
                    }
                  >
                    {vinculoSaving ? 'Guardando…' : 'Guardar vínculo'}
                  </button>
                </div>

                {vinculoFeedback ? (
                  <p
                    className={`crm-ac-vinculos-feedback${
                      vinculoFeedback.includes('No se pudo')
                        ? ' crm-ac-vinculos-feedback--error'
                        : ' crm-ac-vinculos-feedback--ok'
                    }`}
                    role="status"
                  >
                    {vinculoFeedback}
                  </p>
                ) : null}
              </section>

              <section className="crm-ac-vinculos-card crm-ac-vinculos-card--list">
                <header className="crm-ac-vinculos-card__head crm-ac-vinculos-card__head--row">
                  <div className="crm-ac-vinculos-card__head-main">
                    <span className="crm-ac-vinculos-card__icon" aria-hidden="true">
                      🔗
                    </span>
                    <h3 className="crm-ac-vinculos-card__title">Vínculos actuales</h3>
                  </div>
                  <span className="crm-ac-vinculos-card__count">
                    {vinculos.length} vínculo{vinculos.length === 1 ? '' : 's'}
                  </span>
                </header>

                <div className="crm-ac-vinculos-card__body">
              {vinculosLoading ? (
                <div className="crm-ac-vinculos-state" role="status" aria-busy="true">
                  <span className="crm-gd-spinner" aria-hidden="true" />
                  <p className="crm-ac-vinculos-state__title">Cargando vínculos</p>
                </div>
              ) : !vinculos.length ? (
                <div className="crm-ac-vinculos-state" role="status">
                  <span className="crm-ac-vinculos-state__icon" aria-hidden="true">
                    👥
                  </span>
                  <p className="crm-ac-vinculos-state__title">Sin vínculos cargados</p>
                  <p className="crm-ac-vinculos-state__hint">
                    Este titular aún no tiene familiares vinculados.
                  </p>
                </div>
              ) : (
                <>
                <div className="crm-vinculos-mobile-list crm-ac-vinculos-mobile">
                  {vinculos.map((v) => (
                    <article key={`mv-${v.id}`} className="crm-ac-vinculos-mobile-item">
                      <div className="crm-ac-vinculos-mobile-row">
                        <span className="crm-ac-vinculos-mobile-label">Cliente DNI</span>
                        <span className="crm-ac-vinculos-mobile-value">{formatDni(v.cliente_dni) || '—'}</span>
                      </div>
                      <div className="crm-ac-vinculos-mobile-row">
                        <span className="crm-ac-vinculos-mobile-label">Familiar DNI</span>
                        <span className="crm-ac-vinculos-mobile-value crm-ac-vinculos-dni-cell">
                          <span>{formatDni(v.cliente_familiar_dni) || '—'}</span>
                          {v.cliente_familiar_dni ? (
                            <button
                              type="button"
                              className="crm-ac-vinculos-goto-btn"
                              onClick={() => {
                                const familiarDni = String(v.cliente_familiar_dni || '')
                                  .replace(/\D/g, '')
                                  .replace(/^0+/, '');
                                if (!familiarDni) return;
                                navigate(
                                  `/asociacion-clientes?dni=${encodeURIComponent(familiarDni)}&tab=resumen`
                                );
                              }}
                              title="Ir al familiar"
                              aria-label="Ir al familiar"
                            >
                              ↗
                            </button>
                          ) : null}
                        </span>
                      </div>
                      <div className="crm-ac-vinculos-mobile-row">
                        <span className="crm-ac-vinculos-mobile-label">Vínculo</span>
                        <span className="crm-ac-vinculos-mobile-value">
                          {editingVinculoId === v.id ? (
                            <select
                              className="crm-ac-vinculos-select"
                              value={editingVinculoTipo}
                              onChange={(e) => setEditingVinculoTipo(e.target.value)}
                            >
                              <option value="">Seleccionar vínculo</option>
                              {vinculoTipos.map((tipo) => (
                                <option key={`m-edit-tipo-${v.id}-${tipo}`} value={tipo}>
                                  {tipo}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="crm-ac-vinculos-tipo-badge">{v.vinculo || '—'}</span>
                          )}
                        </span>
                      </div>
                      <div className="crm-ac-vinculos-mobile-row">
                        <span className="crm-ac-vinculos-mobile-label">Actualizado</span>
                        <span className="crm-ac-vinculos-mobile-value crm-ac-vinculos-date">
                          {formatDateTime24(v.updated_at) || '—'}
                        </span>
                      </div>
                      <div className="crm-ac-vinculos-mobile-actions">
                        {editingVinculoId === v.id ? (
                          <>
                            <button
                              type="button"
                              className="crm-ac-vinculos-btn crm-ac-vinculos-btn--primary"
                              onClick={() => guardarEdicionVinculo(v.id)}
                              disabled={vinculoSaving || !editingVinculoTipo}
                              title="Guardar edición"
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="crm-ac-vinculos-btn crm-ac-vinculos-btn--ghost"
                              onClick={() => {
                                setEditingVinculoId(null);
                                setEditingVinculoTipo('');
                              }}
                              disabled={vinculoSaving}
                              title="Cancelar edición"
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="crm-ac-vinculos-btn crm-ac-vinculos-btn--ghost"
                            onClick={() => {
                              setEditingVinculoId(v.id);
                              setEditingVinculoTipo(String(v.vinculo || ''));
                            }}
                            title="Editar vínculo"
                          >
                            Editar vínculo
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
                <div className="crm-ac-vinculos-table-wrap">
                  <table className="crm-profile-table crm-ac-vinculos-table">
                    <thead>
                      <tr>
                        <th>Cliente DNI</th>
                        <th>Familiar DNI</th>
                        <th>Vínculo</th>
                        <th>Actualizado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vinculos.map((v) => (
                        <tr key={`v-${v.id}`}>
                          <td data-label="Cliente DNI">{formatDni(v.cliente_dni) || '—'}</td>
                          <td data-label="Familiar DNI">
                            <span className="crm-ac-vinculos-dni-cell">
                              <span>{formatDni(v.cliente_familiar_dni) || '—'}</span>
                              {v.cliente_familiar_dni ? (
                                <button
                                  type="button"
                                  className="crm-ac-vinculos-goto-btn"
                                  onClick={() => {
                                    const familiarDni = String(v.cliente_familiar_dni || '')
                                      .replace(/\D/g, '')
                                      .replace(/^0+/, '');
                                    if (!familiarDni) return;
                                    navigate(
                                      `/asociacion-clientes?dni=${encodeURIComponent(familiarDni)}&tab=vinculos`
                                    );
                                  }}
                                  title="Ir al familiar"
                                  aria-label="Ir al familiar"
                                >
                                  ↗
                                </button>
                              ) : null}
                            </span>
                          </td>
                          <td data-label="Vínculo">
                            {editingVinculoId === v.id ? (
                              <select
                                className="crm-ac-vinculos-select crm-ac-vinculos-select--inline"
                                value={editingVinculoTipo}
                                onChange={(e) => setEditingVinculoTipo(e.target.value)}
                              >
                                <option value="">Seleccionar vínculo</option>
                                {vinculoTipos.map((tipo) => (
                                  <option key={`edit-tipo-${v.id}-${tipo}`} value={tipo}>
                                    {tipo}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="crm-ac-vinculos-tipo-badge">{v.vinculo || '—'}</span>
                            )}
                          </td>
                          <td data-label="Actualizado" className="crm-ac-vinculos-date">
                            {formatDateTime24(v.updated_at) || '—'}
                          </td>
                          <td data-label="Acciones">
                            {editingVinculoId === v.id ? (
                              <div className="crm-ac-vinculos-row-actions">
                                <button
                                  type="button"
                                  className="crm-ac-vinculos-btn crm-ac-vinculos-btn--primary crm-ac-vinculos-btn--sm"
                                  onClick={() => guardarEdicionVinculo(v.id)}
                                  disabled={vinculoSaving || !editingVinculoTipo}
                                  title="Guardar edición"
                                >
                                  Guardar
                                </button>
                                <button
                                  type="button"
                                  className="crm-ac-vinculos-btn crm-ac-vinculos-btn--ghost crm-ac-vinculos-btn--sm"
                                  onClick={() => {
                                    setEditingVinculoId(null);
                                    setEditingVinculoTipo('');
                                  }}
                                  disabled={vinculoSaving}
                                  title="Cancelar edición"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="crm-ac-vinculos-icon-btn"
                                onClick={() => {
                                  setEditingVinculoId(v.id);
                                  setEditingVinculoTipo(String(v.vinculo || ''));
                                }}
                                title="Editar vínculo"
                                aria-label="Editar vínculo"
                              >
                                ✏️
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ========== VISTA LISTA (tabla) ========== */
  return (
    <>
      <section className="crm-gd-page crm-ac-page crm-pbody">
      {/* <div
        style={{
          padding: '6px 28px 0 28px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 8
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: '#7C98B6'
          }}
        >
          Filtrar por cantidad de movimientos:
        </span>
        <select
          value={movCountFilter == null ? '' : String(movCountFilter)}
          onChange={e => {
            const v = e.target.value;
            setMovCountFilter(v === '' ? null : Number(v));
          }}
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 999,
            border: '1px solid #DFE3EB',
            background: '#FFFFFF',
            color: '#253342',
            outline: 'none',
            cursor: 'pointer'
          }}
        >
          <option value="">Todos</option>
          {movCountOptions.map(c => (
            <option key={c} value={c}>
              {c === 1
                ? 'PLEX o GECLISA'
                : c === 2
                  ? 'Por todos'
                  : `${c} movimientos`}
            </option>
          ))}
        </select>
      </div> */}

      {window.innerWidth > 768 ? (
        <div className="crm-ac-kpi-strip" role="group" aria-label="Resumen de métricas">
          {kpis.map((k) => (
            <div
              key={k.id}
              className={`crm-ac-kpi-chip is-${k.tone}`}
              title={`${k.label} — ${k.hint}`}
            >
              <span className="crm-ac-kpi-chip__icon">
                <AcKpiIcon tone={k.tone} />
              </span>
              <div className="crm-ac-kpi-chip__body">
                <span className="crm-ac-kpi-chip__num">{formatKpiNum(k.value)}</span>
                <span className="crm-ac-kpi-chip__label">{k.shortLabel}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {window.innerWidth <= 768 ? (
        <button
          type="button"
          className="crm-ac-kpi-fab"
          onClick={() => setKpisPopupOpen(true)}
        >
          Ver métricas
        </button>
      ) : null}

      <div
        className={`crm-ac-kpi-backdrop${kpisPopupOpen && window.innerWidth <= 768 ? ' is-open' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) setKpisPopupOpen(false); }}
        aria-hidden={!(kpisPopupOpen && window.innerWidth <= 768)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`crm-ac-kpi-popup${kpisPopupOpen && window.innerWidth <= 768 ? ' is-open' : ''}`}
        >
          <div className="crm-ac-kpi-popup__head">
            <div className="crm-gd-drawer-head__title">Resumen de métricas</div>
            <button type="button" className="crm-gd-btn" onClick={() => setKpisPopupOpen(false)}>
              Cerrar
            </button>
          </div>
          <div className="crm-gd-kpi-grid crm-ac-kpi-grid">
            {kpis.map(k => (
              <div key={k.label} className="crm-gd-kpi-card crm-ac-kpi-card">
                <div className="crm-gd-kpi-card__num">{k.value ?? '—'}</div>
                <div className="crm-gd-kpi-card__label">{k.label}</div>
                {k.hint ? (
                  <div className="crm-gd-kpi-card__hint">{k.hint}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="crm-ac-main">
      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right crm-ac-wa-drawer-backdrop${
          waPopupOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'
        }`}
        role="presentation"
        onClick={() => setWaPopupOpen(false)}
        aria-hidden={!waPopupOpen}
      >
        <aside
          className={`crm-gd-drawer-panel crm-ac-wa-drawer${
            waPopupOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="crm-ac-wa-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-ac-wa-drawer__stripe" aria-hidden="true" />

          <div className="crm-ac-wa-drawer__chrome">
            <div className="crm-ac-wa-drawer__headline">
              <span className="crm-ac-wa-drawer__icon-mini" aria-hidden="true">📱</span>
              <h2
                id="crm-ac-wa-title"
                className="crm-ac-wa-drawer__title"
                title="Pacientes dados de alta por WhatsApp pendientes de vinculación. Por defecto, últimos 30 días."
              >
                Altas WhatsApp (sin registrarse en Plex/Geclisa)
              </h2>
              <div className="crm-ac-wa-drawer__chips" role="status">
                <span className="crm-ac-wa-drawer__chip">
                  <strong>{waLoading ? '…' : waTotal}</strong> período
                </span>
                {waSinPlexTotal > 0 ? (
                  <span className="crm-ac-wa-drawer__chip is-alert">
                    <strong>{waSinPlexTotal}</strong> sin registrar
                  </span>
                ) : null}
                {!waLoading && waRows.length > 0 ? (
                  <span className="crm-ac-wa-drawer__chip is-muted">{waPaginationLabel}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="crm-ac-wa-drawer__close"
                onClick={() => setWaPopupOpen(false)}
                aria-label="Cerrar panel"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="crm-ac-wa-drawer__filters-row">
              <input
                type="date"
                className="crm-ac-wa-drawer__date"
                value={waDesde}
                onChange={(e) => setWaDesde(e.target.value)}
                aria-label="Fecha desde"
              />
              <input
                type="date"
                className="crm-ac-wa-drawer__date"
                value={waHasta}
                min={waDesde || undefined}
                onChange={(e) => setWaHasta(e.target.value)}
                aria-label="Fecha hasta"
              />
              <div className="crm-rtz-tablesearch crm-rc-tablesearch crm-ac-wa-drawer__search">
                <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                    <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <input
                  type="text"
                  className="crm-rtz-tablesearch__input"
                  placeholder="Nombre, DNI, tel…"
                  value={waSearch}
                  onChange={(e) => setWaSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyWaFilters();
                    }
                  }}
                  aria-label="Buscar altas WhatsApp"
                />
                {waSearch ? (
                  <button
                    type="button"
                    className="crm-rtz-tablesearch__clear"
                    onClick={() => {
                      setWaSearch('');
                      setWaApplied((prev) => ({ ...prev, search: '' }));
                      setWaPage(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className="crm-rc-icon-btn crm-rc-icon-btn--primary"
                onClick={applyWaFilters}
                disabled={waLoading}
                title="Buscar"
                aria-label="Buscar"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <label className="crm-rtz-tablesize crm-rc-tablesize">
                <div className="crm-rtz-tablesize__wrap">
                  <select
                    className="crm-rtz-tablesize__select"
                    value={String(waPageSize)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setWaPageSize(val === 'all' ? 'all' : (parseInt(val, 10) || 10));
                      setWaPage(1);
                    }}
                    aria-label="Registros por página"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                  <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                </div>
              </label>
            </div>
          </div>

          <div className="crm-ac-wa-drawer__body">
            {waLoading ? (
              <AcTableLoading searchTerm={waApplied.search || waSearch} variant="wa" />
            ) : null}
            {waError && !waLoading ? (
              <AcEmptyHero
                compact
                error
                eyebrow="Altas WhatsApp"
                title="No se pudieron cargar las altas"
                hint={waError}
              />
            ) : null}
            {!waLoading && !waError && waRows.length === 0 ? (
              <AcEmptyHero
                compact
                eyebrow="Canal digital · GECLISA"
                title="No hay altas en el rango seleccionado"
                hint="Probá ampliar el período o cambiar el criterio de búsqueda."
                steps={['1 · Ampliar fechas', '2 · Refinar búsqueda', '3 · Vincular en sanatorio']}
              />
            ) : null}
            {!waLoading && !waError && waRows.length > 0 ? (
              <div className="crm-ac-wa-table-body-scroll crm-ac-wa-drawer__table">
                {waSinPlexTotal > 0 ? (
                  <div className="crm-ac-wa-alert-strip" role="alert">
                    <span className="crm-ac-wa-alert-strip__icon" aria-hidden>⚠</span>
                    <div className="crm-ac-wa-alert-strip__copy">
                      <strong>{waSinPlexTotal} paciente{waSinPlexTotal === 1 ? '' : 's'} sin ficha en Plex/Geclisa</strong>
                      <span>Se listan primero. Debés darlos de alta en el sanatorio para vincularlos.</span>
                    </div>
                  </div>
                ) : null}
                <PaginatedTable
                  columns={waColumns}
                  data={waRows}
                  page={waPage}
                  totalPages={waTotalPages}
                  onPageChange={setWaPage}
                  pageSize={waEffectiveLimit}
                  paginationLabel={waPaginationLabel}
                  exportName="altas_whatsapp"
                  tableClassName="crm-ac-datatable crm-ac-wa-datatable"
                  flatWrapper
                />
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div
        className={`crm-ac-wa-overlay${waPlexPopupOpen ? ' is-open' : ''}`}
        role="presentation"
        onClick={() => setWaPlexPopupOpen(false)}
      >
        <div
          className="crm-ac-wa-modal crm-ac-wa-modal--plex"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crm-ac-wa-plex-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="crm-ac-wa-modal__head">
            <div className="crm-ac-wa-modal__head-accent crm-ac-wa-modal__head-accent--plex" aria-hidden="true" />
            <div className="crm-ac-wa-modal__head-row">
              <div className="crm-ac-wa-modal__head-copy">
                <p className="crm-ac-wa-modal__eyebrow">Bot Farmacia · Registro externo PLEX</p>
                <h2 id="crm-ac-wa-plex-title" className="crm-ac-wa-modal__title">
                  Altas WhatsApp PLEX
                </h2>
                <p className="crm-ac-wa-modal__sub">
                  Clientes registrados por el bot de Farmacia que aún no están en la asociación
                  PLEX ↔ GECLISA. Por defecto se muestran los últimos 30 días.
                </p>
              </div>
              <button
                type="button"
                className="crm-ac-wa-modal__close"
                onClick={() => setWaPlexPopupOpen(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="crm-ac-wa-filters">
              <div className="crm-ac-wa-filters__dates">
                <label className="crm-ac-wa-field">
                  <span className="crm-ac-wa-field__label">Desde</span>
                  <input
                    type="date"
                    className="crm-ac-wa-field__input"
                    value={waPlexDesde}
                    onChange={(e) => setWaPlexDesde(e.target.value)}
                  />
                </label>
                <label className="crm-ac-wa-field">
                  <span className="crm-ac-wa-field__label">Hasta</span>
                  <input
                    type="date"
                    className="crm-ac-wa-field__input"
                    value={waPlexHasta}
                    onChange={(e) => setWaPlexHasta(e.target.value)}
                  />
                </label>
              </div>
              <div className="crm-ac-wa-filters__search">
                <CwListSearch
                  value={waPlexSearch}
                  onChange={(e) => setWaPlexSearch(e.target.value)}
                  placeholder="Buscar por nombre, DNI, teléfono, email…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyWaPlexFilters();
                    }
                  }}
                />
                <button
                  type="button"
                  className={`crm-gd-btn crm-gd-btn--primary${waPlexLoading ? ' is-busy' : ''}`}
                  onClick={applyWaPlexFilters}
                  disabled={waPlexLoading}
                >
                  {waPlexLoading ? 'Buscando…' : 'Buscar'}
                </button>
              </div>
            </div>

            <div className="crm-ac-wa-toolbar-meta">
              <label className="crm-ac-wa-page-size">
                <span>Mostrar</span>
                <select
                  value={String(waPlexPageSize)}
                  onChange={(e) => {
                    setWaPlexPageSize(parseInt(e.target.value, 10) || 10);
                    setWaPlexPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                <span>registros por página</span>
              </label>
              <span className="crm-ac-wa-count">
                <strong>{waPlexTotal}</strong> registro{waPlexTotal === 1 ? '' : 's'} en el período
              </span>
            </div>
          </header>

          <div className="crm-ac-wa-body">
            {waPlexLoading ? (
              <div className="crm-gd-loading crm-ac-wa-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="crm-gd-spinner" aria-hidden="true" />
                <div className="crm-gd-loading__title">Cargando altas WhatsApp PLEX…</div>
              </div>
            ) : null}
            {waPlexError && !waPlexLoading ? (
              <AcEmptyHero
                compact
                error
                eyebrow="Bot Farmacia · PLEX"
                title="No se pudieron cargar las altas"
                hint={waPlexError}
              />
            ) : null}
            {!waPlexLoading && !waPlexError && waPlexRows.length === 0 ? (
              <AcEmptyHero
                compact
                eyebrow="Bot Farmacia · PLEX"
                title="No hay altas PLEX en el rango seleccionado"
                hint="Probá ampliar el período o cambiar el criterio de búsqueda."
                steps={['1 · Ampliar fechas', '2 · Refinar búsqueda', '3 · Asociar cliente']}
              />
            ) : null}
            {!waPlexLoading && !waPlexError && waPlexRows.length > 0 ? (
              <div className="crm-ac-wa-table-body-scroll">
                <PaginatedTable
                  columns={waPlexColumns}
                  data={waPlexRows}
                  page={waPlexPage}
                  totalPages={waPlexTotalPages}
                  onPageChange={setWaPlexPage}
                  exportName="altas_whatsapp_plex"
                  tableClassName="crm-ac-datatable"
                  flatWrapper
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

        <div className="crm-ac-data">
          <div className="crm-ac-table-wrap crm-tscroll crm-tscroll-clickable-rows">
            <div className="crm-rc-filters crm-ac-filters">
              <div className="crm-rc-filters__row crm-ac-filters__row">
                <div className="crm-ac-filters__right">
                <span className="crm-rc-filters__meta">
                  <strong>{loading ? '…' : filteredRows.length}</strong>/<strong>{loading ? '…' : total}</strong>
                  {debouncedSearch ? <> · Búsqueda activa</> : null}
                </span>
                <div className="crm-rtz-tablesearch crm-rc-tablesearch crm-ac-tablesearch">
                  <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="crm-rtz-tablesearch__input"
                    placeholder="Nombre, DNI, cobertura…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        triggerSearchNow();
                      }
                    }}
                    aria-label="Buscar en la asociación"
                  />
                  {search || debouncedSearch ? (
                    <button
                      type="button"
                      className="crm-rtz-tablesearch__clear"
                      onClick={() => {
                        setSearch('');
                        setDebouncedSearch('');
                        setPage(1);
                      }}
                      aria-label="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="crm-rc-icon-btn crm-rc-icon-btn--primary"
                  onClick={triggerSearchNow}
                  disabled={loading}
                  title="Buscar en PLEX y GECLISA"
                  aria-label="Buscar"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                    <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <label className="crm-rtz-tablesize crm-rc-tablesize crm-ac-tablesize">
                  <div className="crm-rtz-tablesize__wrap">
                    <select
                      className="crm-rtz-tablesize__select"
                      value={tablePageSize <= 0 ? 'ALL' : String(tablePageSize)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'ALL') {
                          setTablePageSize(0);
                          setPage(1);
                          return;
                        }
                        const n = parseInt(v, 10);
                        setTablePageSize(Number.isNaN(n) ? PAGE_SIZE : n);
                        setPage(1);
                      }}
                      aria-label="Registros por página"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="ALL">Todas</option>
                    </select>
                    <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                  </div>
                </label>
                <button
                  type="button"
                  className="crm-ac-wa-trigger crm-ac-wa-trigger--inline"
                  onClick={openWaPopup}
                  title="Registros captados por WhatsApp pendientes de vinculación con el sanatorio"
                  aria-label={
                    waTriggerCountLoading
                      ? 'Altas WhatsApp, cargando cantidad'
                      : `Altas WhatsApp, ${waTriggerCount} registro${waTriggerCount === 1 ? '' : 's'}`
                  }
                >
                  <span className="crm-ac-wa-trigger__icon" aria-hidden="true">📱</span>
                  <span className="crm-ac-wa-trigger__label">Altas WhatsApp</span>
                  <span
                    className={`crm-ac-wa-trigger__badge${
                      waTriggerCountLoading ? ' is-loading' : ''
                    }${!waTriggerCountLoading && waTriggerCount === 0 ? ' is-zero' : ''}${
                      !waTriggerCountLoading && waTriggerSinPlex > 0 ? ' is-alert' : ''
                    }`}
                    aria-hidden="true"
                  >
                    {waTriggerCountLoading ? '…' : waTriggerCount}
                  </span>
                </button>
                </div>
              </div>
            </div>

            {loading ? (
              <AcTableLoading searchTerm={debouncedSearch} />
            ) : null}

            {!loading && filteredRows.length === 0 ? (
              <AcEmptyHero
                eyebrow="PLEX ↔ GECLISA"
                title={debouncedSearch ? 'No se encontraron clientes' : 'Sin datos de asociación'}
                hint={
                  debouncedSearch
                    ? 'No hay coincidencias con ese criterio. Probá con otro nombre, DNI u obra social.'
                    : 'Todavía no hay registros de asociación para mostrar en este momento.'
                }
                steps={
                  debouncedSearch
                    ? ['1 · Limpiar búsqueda', '2 · Probar otro DNI', '3 · Ampliar criterio']
                    : ['1 · Buscar por DNI', '2 · Filtrar por cobertura', '3 · Revisar cruces']
                }
              />
            ) : null}

            {!loading && filteredRows.length > 0 ? (
              <PaginatedTable
                flatWrapper
                columns={columns}
                extraColumns={extraColumns}
                data={filteredRows}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                onRowClick={undefined}
                exportName="asociacion_clientes"
                tableClassName="crm-ac-datatable"
                paginationLabel={`Pag ${page} de ${totalPages}`}
              />
            ) : null}
          </div>
        </div>
      </div>
      </section>
    </>
  );
}

export default AsociacionClientes;
